import {
  addGeneralMeetingDocuments,
  addSubscriptionFormDocument,
  ArticlesOfAssociationChangeMode,
  CleanupFunction,
  DbotEvent,
  DbotEventDispatcher,
  DecisionContext,
  ensureShareTypeCodeInSharesForShareTypes,
  extractSectionWithMaxScoreFromArticles,
  FollowUpType,
  IncreaseOfShareCapitalSubscribersStatus,
  IncreaseOfShareCapitalSubscribersStatusForIssue,
  IncreaseOfShareCapitalSubscribersStatusForNominalValue,
  InputState,
  isLanguageChanged,
  NumberOfSharesForShareType,
  ShareAllocation,
  ShareAllocationRound,
  ShareAllocationRoundsResponse,
  SharesForShareholder,
  total,
  updateArticlesModeModel,
  updateFollowUpStatusEvent,
} from "../common/commonDomain";
import { autorun, makeAutoObservable, runInAction } from "mobx";
import Big from "big.js";
import {
  ExtendedShareholderDTO,
  ShareholderData,
  transformShareholder,
} from "../../types/Shareholder";
import {
  Failure,
  isOk,
  LocalizedText,
  ok,
  Ok,
  pending,
  Result,
  signer,
  Signer,
  SignerType,
  SpecialCircumstancesType,
} from "../../common/common";
import { newUuid } from "../../utils/uuid";
import { compact, debounce, identity, isEqual, partition, uniq } from "lodash";
import { hasShares } from "../../stores/ShareholdersStore";
import { CURRENCY_NOK } from "../../utils/currency";
import { ReactNode } from "react";
import { getFullName, validateName } from "../../utils/Roles";
import { SuggestedCompany } from "../../types/CompanyInfo";
import { noDialog } from "../summary/summaryDomain";
import { percentOf } from "../../utils/numbers";
import { Language } from "../../i18n/languages";
import { isNotBlank } from "../../utils/strings";
import { TMessage } from "../../i18n/tt";
import { DecisionType } from "../../common/decisions";
import { ChangeArticlesOfAssociationModel } from "../changeArticlesOfAssociation/changeArticlesOfAssociationDomain";
import { toShareholderReference, toSubscriberIdReference } from "../../utils/UrnReference";
import {
  ArticleSelectorModel,
  BooleanFieldModel,
  createEmailField,
  LocalDateFieldModel,
  LocalizedStringFieldModel,
  NumberFieldModel,
  OptionFieldModel,
  RadioFieldModel,
  StringFieldModel,
} from "../../common/commonForms";
import { ShareTypeCode, shareTypeLabel } from "../../common/share";
import { plusDays, today, toLocalDateISOString } from "../../utils/Dates";
import { shareholderCommitmentWhitelist } from "../../common/featureToggles";
import {
  actorsApi,
  ActorsSelectorModel,
  ActorType,
  loadActors,
  SelectedActor,
} from "../../types/SelectActors";

export type IncreaseOfShareCapital = {
  type: DecisionType.increaseOfShareCapital;
  quickSetupType?: QuickSetupType;
  properties: ShareCapitalIncreaseType;
  isSubscriptionForm?: boolean;
  isOtherSigningForAllSubscribers?: boolean;
  signersForAllSubscribers?: SubscriberSigner[];
  periodForSubscription?: PeriodForSubscription;
  deadlineForSubscription?: string;
  isDisposableAccount?: boolean;
  isAccountGiven?: boolean;
  accountNumber?: string;
  usePowerOfAttorney?: boolean;
  powerOfAttorneyDate?: string;
  powerOfAttorneyText?: string;
  estimatedCosts?: number;
  specialCircumstancesUponSubscription?: SpecialCircumstancesType;
  specialCircumstancesUponSubscriptionTexts: LocalizedText[];
  specialCircumstancesAfterLastApprovedAnnualAccount?: SpecialCircumstancesType;
  specialCircumstancesAfterLastApprovedAnnualAccountTexts: LocalizedText[];
  contributionTypes: ContributionType[];
  excessAmountMethod?: ExcessAmountMethod;
  updateArticlesAutomatically?: boolean;
  articleOfAssociationNumber?: number;
  articleChanges: ArticlesOfAssociationChangeMode[];
  updateCommitmentAutomatically?: boolean;
};

export enum QuickSetupType {
  CASH_PREFERENTIAL_RIGHTS = "CASH_PREFERENTIAL_RIGHTS",
  CASH_PRIVATE_PLACEMENT = "CASH_PRIVATE_PLACEMENT",
}

export enum IncreaseOfShareCapitalType {
  shareIssue = "shareIssue",
  increaseNominalValue = "increaseNominalValue",
}

const increaseOfShareCapitalTypeCaption: { [key in IncreaseOfShareCapitalType]: TMessage } = {
  [IncreaseOfShareCapitalType.shareIssue]: {
    key: "decisions.increaseShareCapitalAgainstCash.sharesOrNominalValue.newShares",
  },
  [IncreaseOfShareCapitalType.increaseNominalValue]: {
    key: "decisions.increaseShareCapitalAgainstCash.sharesOrNominalValue.increaseNominalValue",
  },
};
export type ShareCapitalIncreaseType = ShareIssue | IncreaseNominalValue;

export type ShareIssue = {
  type: IncreaseOfShareCapitalType.shareIssue;
  nominalValue?: number;
  shareIssuePerShareTypes: ShareIssuePerShareType[];
  subscribers: SubscriberForIssue[];
  preferentialIssueSubscribers: SubscriberForIssue[];
  preferentialRight?: boolean;
  preferentialRightTransferable?: boolean;
  preferentialRightOversubscribe?: boolean;
  preferentialBoardRightsToSellSubscription?: boolean;
  externalSubscribers?: boolean;
  preferentialRightWaivedReason?: PreferentialRightWaivedType;
  preferentialRightWaivedReasonText?: string;
  shareholderRights?: ShareholderRights;
  isGuaranteeConsortiumEstablished?: boolean;
  guaranteeConsortiumCommission?: GuaranteeConsortiumCommission;
};

export type ShareIssuePerShareType = {
  sharetypeId: number;
  sharetypeCode: ShareTypeCode;
  issueMode: ShareIssueMode;
  sharePrice?: number;
  isNewShareType: boolean;
};

export type IncreaseNominalValue = {
  type: IncreaseOfShareCapitalType.increaseNominalValue;
  increaseInNominalValue?: number;
  sharePrice?: number;
  contributionPerShareTypes: Array<ContributionPerShareType>;
  subscribers?: Array<SubscriberForIncreaseNominalValue>;
};

export type SubscriberId = string;

export type SubscriberForIncreaseNominalValue = {
  subscriberid: SubscriberId;
  shareholderId: number;
  name: string;
  address?: string;
  cashContribution?: CashContribution;
  debtContribution?: DebtContribution | null;
  kindContribution?: KindContribution | null;
  signers: SubscriberSigner[];
};

export type ShareIssueMode = Fixed | Interval;

export type Fixed = { type: "fixed"; numberOfShares?: number };
export type Interval = { type: "interval"; from?: number; to?: number };

export type ContributionPerShareType = {
  contributionPerShare?: number;
  sharetypeId: number;
  name: string;
};

export type PeriodForSubscription = {
  from?: string;
  to?: string;
};

export type GuaranteeConsortiumCommission = {
  numberOfShares?: number;
  percentOfGuaranteedAmount?: number;
};

export enum PreferentialRightWaivedType {
  SHAREHOLDERS_IN_UNDERSTANDING = "SHAREHOLDERS_IN_UNDERSTANDING",
  DEEMED_NECESSARY = "DEEMED_NECESSARY",
  ADDED_EXPLANATION = "ADDED_EXPLANATION",
}

export enum ShareholderRights {
  FROM_REGISTRATION_DATE = "FROM_REGISTRATION_DATE",
  FROM_SUBSCRIPTION_DATE = "FROM_SUBSCRIPTION_DATE",
}

export function getNewNominalValue(nominalValue: number, increase: number) {
  return Big(nominalValue).plus(increase).toNumber();
}

export function isNominalValueValid(nominalValue: number) {
  const multiplier = 10000;
  return Big(nominalValue).times(multiplier).mod(1).toNumber() === 0;
}

export function isShareCapitalValid(numberOfShares: number, nominalValue: number) {
  const multiplier = 1000000000;
  const newShareCapital = Big(numberOfShares).times(nominalValue);
  return Big(newShareCapital).times(multiplier).mod(1).toNumber() === 0;
}

export type CashContribution = {
  type: ContributionType.cash;
  amount: number;
};

export type DebtContribution = {
  type: ContributionType.debt;
  maxAmountToBeConverted: number;
  totalAmountReceivable: number;
  description: string;
};

export type KindContribution = {
  type: ContributionType.kind;
  value: number;
  description: LocalizedText[];
};

export enum ContributionType {
  cash = "cash",
  debt = "debt",
  kind = "kind",
}

export function contributionTypeLabel(type: ContributionType): TMessage {
  switch (type) {
    case ContributionType.cash:
      return { key: `decisions.increaseShareCapitalAgainstCash.cash` };
    case ContributionType.debt:
      return { key: `decisions.increaseShareCapitalAgainstCash.debt` };
    case ContributionType.kind:
      return { key: `decisions.increaseShareCapitalAgainstCash.kind` };
  }
}

export enum ExcessAmountMethod {
  discharged = "discharged",
  remains_liability = "remains_liability",
  paidout = "paidout",
}

export type ExcessAmountMethodOption = {
  value: ExcessAmountMethod;
  label: TMessage;
};

const specialCircumstancesCaptions: { [key in SpecialCircumstancesType]: TMessage } = {
  [SpecialCircumstancesType.NONE]: {
    key: "decisions.increaseShareCapitalAgainstCash.circumstancesAfterApprovedAnnualAccount.noReason",
  },
  [SpecialCircumstancesType.NONE_UNKNOWN_TO_SUBSCRIBERS]: {
    key: "decisions.increaseShareCapitalAgainstCash.circumstances.noUnknownReason",
  },
  [SpecialCircumstancesType.ADDED_EXPLANATION]: {
    key: "decisions.increaseShareCapitalAgainstCash.circumstancesDescription",
  },
};

export class IncreaseOfShareCapitalForm {
  readonly type = DecisionType.increaseOfShareCapital;
  quickSetupType?: QuickSetupType;
  quickSetupModel?: QuickSetupModel;
  inputState?: InputState;
  shareIssue: ShareIssueModel;
  increaseNominalValue: IncreaseNominalValueModel;
  properties: ShareCapitalIncreaseTypeModel;
  isSubscriptionForm: BooleanFieldModel;
  isOtherSigningForAllSubscribers: BooleanFieldModel;
  signersForAllSubscribers: SubscriberSigner[];
  periodForSubscription: PeriodForSubscriptionModel;
  deadlineForSubscription: LocalDateFieldModel;
  isDisposableAccount: BooleanFieldModel;
  isAccountGiven: BooleanFieldModel;
  accountNumber: StringFieldModel;
  usePowerOfAttorney: BooleanFieldModel;
  powerOfAttorneyDate: LocalDateFieldModel;
  powerOfAttorneyText: StringFieldModel;
  estimatedCosts: NumberFieldModel;
  specialCircumstancesUponSubscriptionModel: RadioFieldModel<SpecialCircumstancesType | undefined>;
  specialCircumstancesUponSubscriptionTexts: LocalizedStringFieldModel[];
  specialCircumstancesAfterLastApprovedAnnualAccountModel: RadioFieldModel<
    SpecialCircumstancesType | undefined
  >;
  increaseOfShareCapitalTypeModel: RadioFieldModel<IncreaseOfShareCapitalType | undefined>;
  articleOfAssociationNumber: ArticleSelectorModel;
  specialCircumstancesAfterLastApprovedAnnualAccountTexts: LocalizedStringFieldModel[];
  contributionTypes: OptionFieldModel<ContributionType>;
  excessAmountMethod?: ExcessAmountMethod;
  excessAmountMethodOptions: ExcessAmountMethodOption[] = [
    {
      value: ExcessAmountMethod.discharged,
      label: { key: "decisions.increaseShareCapitalAgainstCash.discharged" },
    },
    {
      value: ExcessAmountMethod.remains_liability,
      label: { key: "decisions.increaseShareCapitalAgainstCash.remains_liability" },
    },
    {
      value: ExcessAmountMethod.paidout,
      label: { key: "decisions.increaseShareCapitalAgainstCash.paidout" },
    },
  ];
  subscribersStatus?: ViewSubscribersDialogModel;
  updateArticlesAutomatically: RadioFieldModel<boolean | undefined>;
  articleChangeModel: ChangeArticlesOfAssociationModel;
  updateCommitmentAutomatically: RadioFieldModel<boolean | undefined>;

  addSignerForAllSubscribersModel?: { nameField: StringFieldModel; emailField: StringFieldModel };

  readonly decisionId: string;

  constructor(
    readonly dispatch: DbotEventDispatcher,
    readonly context: DecisionContext,
    public data?: IncreaseOfShareCapital,
  ) {
    makeAutoObservable(this, { type: false, dispatch: false }, { autoBind: true });
    this.decisionId = context.decisionId;
    this.inputState = context.inputState;

    this.quickSetupType = data?.quickSetupType;

    this.shareIssue = new ShareIssueModel(
      this.dispatch,
      context,
      this.updateRequiredDependencies,
      asShareIssue(this.data?.properties),
    );
    this.increaseNominalValue = new IncreaseNominalValueModel(
      context,
      asIncreaseNominalValue(this.data?.properties),
    );
    this.properties =
      this.data?.properties.type === "increaseNominalValue"
        ? this.increaseNominalValue
        : this.shareIssue;
    this.isSubscriptionForm = new BooleanFieldModel({
      value: data?.isSubscriptionForm,
      required: true,
      onChange: this.updateRequiredDependencies,
    });
    this.isOtherSigningForAllSubscribers = new BooleanFieldModel({
      value: data?.isOtherSigningForAllSubscribers,
    });
    this.signersForAllSubscribers = data?.signersForAllSubscribers ?? [];
    this.periodForSubscription = new PeriodForSubscriptionModel(data?.periodForSubscription);
    this.deadlineForSubscription = new LocalDateFieldModel({
      value: data?.deadlineForSubscription,
      required: true,
    });
    this.isDisposableAccount = new BooleanFieldModel({
      value: data?.isDisposableAccount ?? true,
      required: true,
    });
    this.isAccountGiven = new BooleanFieldModel({
      value: data?.isAccountGiven ?? false,
      required: true,
      onChange: this.updateRequiredDependencies,
    });
    this.accountNumber = new StringFieldModel({ value: data?.accountNumber });
    this.usePowerOfAttorney = new BooleanFieldModel({
      value: data?.usePowerOfAttorney,
      required: true,
      onChange: this.updateRequiredDependencies,
    });
    this.powerOfAttorneyDate = new LocalDateFieldModel({ value: data?.powerOfAttorneyDate });
    this.powerOfAttorneyText = new StringFieldModel({ value: data?.powerOfAttorneyText });
    this.estimatedCosts = new NumberFieldModel({
      value: data?.estimatedCosts ?? 0,
      isPositiveOrZero: true,
      required: true,
    });
    this.specialCircumstancesUponSubscriptionTexts = createTextModels(
      data?.specialCircumstancesUponSubscriptionTexts,
    );
    this.specialCircumstancesAfterLastApprovedAnnualAccountTexts = createTextModels(
      data?.specialCircumstancesAfterLastApprovedAnnualAccountTexts,
    );
    this.articleOfAssociationNumber = new ArticleSelectorModel(
      context.inputState?.articles,
      data?.articleOfAssociationNumber,
    );
    this.increaseOfShareCapitalTypeModel = new RadioFieldModel<
      IncreaseOfShareCapitalType | undefined
    >({
      value: data?.properties?.type,
      options: Object.values(IncreaseOfShareCapitalType),
      idFn: identity,
      labelFn: (value) => (value ? increaseOfShareCapitalTypeCaption[value] : { translated: "" }),
      onChange: (value) => this.changeShareCapitalIncreaseType(value as IncreaseOfShareCapitalType),
    });

    this.contributionTypes = new OptionFieldModel<ContributionType>({
      options: Object.values(ContributionType),
      value: Object.values(ContributionType).filter((e) => data?.contributionTypes?.includes(e)),
      idFn: (option) => option,
      labelFn: (option) => contributionTypeLabel(option),
      required: true,
      onChange: this.updateRequiredDependencies,
    });
    this.excessAmountMethod = data?.excessAmountMethod;
    this.specialCircumstancesUponSubscriptionModel = new RadioFieldModel<
      SpecialCircumstancesType | undefined
    >({
      value: data?.specialCircumstancesUponSubscription ?? SpecialCircumstancesType.NONE,
      options: Object.values(SpecialCircumstancesType),
      idFn: identity,
      labelFn: (value) => (value ? specialCircumstancesCaptions[value] : { translated: "" }),
      onChange: this.updateRequiredDependencies,
    });
    this.specialCircumstancesAfterLastApprovedAnnualAccountModel = new RadioFieldModel<
      SpecialCircumstancesType | undefined
    >({
      value:
        data?.specialCircumstancesAfterLastApprovedAnnualAccount ?? SpecialCircumstancesType.NONE,
      options: Object.values(SpecialCircumstancesType),
      idFn: identity,
      labelFn: (value) => (value ? specialCircumstancesCaptions[value] : { translated: "" }),
      onChange: this.updateRequiredDependencies,
    });
    this.updateArticlesAutomatically = updateArticlesModeModel(
      this.data?.updateArticlesAutomatically,
    );
    this.articleChangeModel = new ChangeArticlesOfAssociationModel(
      this.context,
      data?.articleChanges,
    );
    this.updateCommitmentAutomatically = new RadioFieldModel<boolean | undefined>({
      value: data?.updateCommitmentAutomatically,
      options: [false, true],
      idFn: (value) => (value === undefined ? "undefined" : String(value)),
      labelFn: (value) =>
        value === true
          ? {
              key: "decisions.increaseShareCapitalAgainstCash.updateCommitmentAutomatically.automatic",
            }
          : value === false
            ? {
                key: "decisions.increaseShareCapitalAgainstCash.updateCommitmentAutomatically.manual",
              }
            : { translated: "" },
    });
    this.updateRequiredDependencies();
    //end IncreaseOfShareCapitalForm
  }

  get isCapitalCallEnabled() {
    return shareholderCommitmentWhitelist.includes(this.context.orgNo);
  }

  get currentNominalValue() {
    return this.inputState?.sharenominalValue || 0;
  }
  get currentNumberOfShares() {
    return total(this.inputState?.numberOfShares ?? []);
  }

  updateRequiredDependencies() {
    this.periodForSubscription.setRequired(this.isSubscriptionForm.value);
    this.accountNumber.setRequired(this.isAccountGiven.value && this.containsCash);
    this.powerOfAttorneyDate.setRequired(this.usePowerOfAttorney.value);
    this.powerOfAttorneyText.setRequired(this.usePowerOfAttorney.value);
    this.specialCircumstancesUponSubscriptionTexts.forEach((t) =>
      t.model.setRequired(
        this.specialCircumstancesUponSubscriptionModel.value ===
          SpecialCircumstancesType.ADDED_EXPLANATION,
      ),
    );
    this.specialCircumstancesAfterLastApprovedAnnualAccountTexts.forEach((t) =>
      t.model.setRequired(
        this.specialCircumstancesAfterLastApprovedAnnualAccountModel.value ===
          SpecialCircumstancesType.ADDED_EXPLANATION,
      ),
    );

    // Validate for properties will just check either increaseNominalValue or shareIssue
    this.shareIssue.preferentialRightTransferable.setRequired(this.isPreferentialRight);
    this.shareIssue.preferentialRightOversubscribe.setRequired(this.isPreferentialRight);
    this.shareIssue.preferentialBoardRightsToSellSubscription.setRequired(this.isPreferentialRight);
    this.shareIssue.guaranteeConsortiumCommission.setRequired(
      this.shareIssue.isGuaranteeConsortiumEstablished.value && this.containsCash,
    );
    this.shareIssue.preferentialRightWaivedReasonModel.setRequired(
      !this.isPreferentialRight && this.containsCash,
    );
    this.shareIssue.nominalValue.setRequired(this.currentNumberOfShares === 0);
  }

  init(): CleanupFunction {
    if (this.data === undefined || this.data.articleOfAssociationNumber === undefined) {
      this.dispatch({
        type: "dbot/explore-articles-of-association",
        articles: this.inputState?.articles,
      });
    }
    return autorun(() => {
      const languages = uniq(this.context.currentDocuments.flatMap((d) => d.languages ?? []));
      if (isLanguageChanged(languages, this.specialCircumstancesUponSubscriptionTexts)) {
        runInAction(() => {
          this.specialCircumstancesUponSubscriptionTexts = updateTextModels(
            languages,
            this.specialCircumstancesUponSubscriptionTexts,
          );
          this.specialCircumstancesAfterLastApprovedAnnualAccountTexts = updateTextModels(
            languages,
            this.specialCircumstancesAfterLastApprovedAnnualAccountTexts,
          );
        });
      }
      if (
        [
          ...this.shareIssue.subscribers,
          ...this.shareIssue.preferentialIssueSubscribers,
          ...this.increaseNominalValue.subscribers,
        ].some((s) => isLanguageChanged(languages, s.kindContributionDescriptionField))
      ) {
        runInAction(() => {
          this.shareIssue.onLanguageUpdate(languages);
          this.increaseNominalValue.onLanguageUpdate(languages);
        });
      }
    });
  }

  handleEvent(event: DbotEvent) {
    switch (event.type) {
      case "dbot/update-input-state": {
        const inputState = this.context.inputState;
        if (!isEqual(this.inputState, inputState)) {
          this.inputState = inputState;
          this.shareIssue.onInputStateChange(inputState);
          this.increaseNominalValue.onInputStateChange(inputState);
          this.articleChangeModel.updateArticles(inputState?.articles);
        }
        break;
      }
      case "dbot/fetch-increase-of-share-capital-subscription-form-signers": {
        this.dispatch({
          type: "dbot/fetch-increase-of-share-capital-subscription-form-signers-result",
          signers: this.activeSubscribers.flatMap((s) => s.toSigners()),
          decisionId: this.decisionId,
        });
        break;
      }
      case "dbot/fetch-increase-of-share-capital-subscribers-status": {
        this.dispatch({
          type: "dbot/fetch-increase-of-share-capital-subscribers-status-result",
          data: this.toSubscribersStatus(),
        });

        break;
      }
      case "dbot/addIncreaseOfShareCapitalSubscribersForIssue": {
        this.shareIssue.setSubscribersModel(event.subscribers, this.isPreferentialRight);
        this.setSignersForAllSubscribers(event.signersForAllSubscribers);
        this.validateActiveSubscribers();
        break;
      }
      case "dbot/addIncreaseOfShareCapitalSubscribersForNominalValue": {
        this.increaseNominalValue.setSubscriberModel(event.subscribers);
        this.setSignersForAllSubscribers(event.signersForAllSubscribers);
        this.validateActiveSubscribers();
        break;
      }
      case "dbot/explore-articles-of-association-success": {
        if (this.articleOfAssociationNumber.selected === undefined) {
          this.articleOfAssociationNumber.setSelected(
            extractSectionWithMaxScoreFromArticles(event.data.results, "SHARE_CAPITAL"),
          );
        }
        break;
      }
      case "dbot/search-companies-result":
        if (event.source === this.shareIssue) {
          this.shareIssue.searchCompany?.setCompanies(event.result);
        } else {
          this.subscribersStatus?.subscriberModel.handleEvent(event);
        }
        break;
      default:
        this.subscribersStatus?.subscriberModel.handleEvent(event);
        break;
    }
  }

  setSignersForAllSubscribers(signers: SubscriberSigner[]) {
    if (signers.length > 0) {
      this.isSubscriptionForm.setValue(false);
      this.isOtherSigningForAllSubscribers.setValue(true);
      this.signersForAllSubscribers = this.signersForAllSubscribers.concat(signers);
    }
  }

  addSignerForAllSubscribers() {
    this.addSignerForAllSubscribersModel = {
      nameField: new StringFieldModel({ required: true, validator: validateName }),
      emailField: new StringFieldModel({ required: true }),
    };
  }

  cancelSignerForAllSubscribers() {
    this.addSignerForAllSubscribersModel = undefined;
  }

  submitSignerForAllSubscribers() {
    if (!this.addSignerForAllSubscribersModel) {
      return;
    }
    const { nameField, emailField } = this.addSignerForAllSubscribersModel;
    nameField.validate();
    emailField.validate();
    if (nameField.isValid && emailField.isValid) {
      this.signersForAllSubscribers.push({
        id: newUuid(),
        name: nameField.validatedValue,
        email: emailField.validatedValue!,
      });
      this.addSignerForAllSubscribersModel = undefined;
    }
  }

  removeSignerForAllSubscribers(signer: SubscriberSigner) {
    this.signersForAllSubscribers.splice(this.signersForAllSubscribers.indexOf(signer), 1);
  }

  closeSubscribersStatusDialog() {
    this.subscribersStatus = undefined;
  }

  openSubscribersStatusDialog() {
    this.subscribersStatus = {
      type: "status",
      subscriberModel: new SubscribersStatusModel(
        this.dispatch,
        this.closeSubscribersStatusDialog,
        this.context.orgNo,
        this.context.shareholderData,
        this.context.documentLanguages,
        this.context.isLocked,
        this.inputState,
        this.toSubscribersStatus(),
      ),
    };
  }

  toSubscribersStatus(): IncreaseOfShareCapitalSubscribersStatus {
    switch (this.properties.type) {
      case IncreaseOfShareCapitalType.shareIssue: {
        return this.toSubscribersStatusForIssue();
      }
      case IncreaseOfShareCapitalType.increaseNominalValue: {
        return this.toSubscribersStatusForNominalValue();
      }
    }
  }

  toSubscribersStatusForIssue(): IncreaseOfShareCapitalSubscribersStatusForIssue {
    return {
      type: IncreaseOfShareCapitalType.shareIssue,
      selectedShareTypes: toShareIssueForShareTypes(this.shareIssue),
      subscribers: this.subscribersForIssueCandidates.map((s) => toSubscriberForIssue(s)),
      isPreferentialRights: this.isPreferentialRight,
      isOverSubscriptionAllowed: this.shareIssue.preferentialRightOversubscribe.value ?? false,
      subscriptionPeriod: toPeriodForSubscription(this.periodForSubscription),
    };
  }

  toSubscribersStatusForNominalValue(): IncreaseOfShareCapitalSubscribersStatusForNominalValue {
    return {
      type: IncreaseOfShareCapitalType.increaseNominalValue,
      contributionPerShareType: this.increaseNominalValue.contributionPerShareTypes.map((s) =>
        s.toModel(),
      ),
      subscribers: this.increaseNominalValue.subscribers.map((s) =>
        toSubscriberForIncreaseNominalValue(s),
      ),
    };
  }

  // Receives only the subscribers who is a part of the share capital increase
  get activeSubscribers(): SubscriberForCapitalIncrease[] {
    switch (this.properties.type) {
      case IncreaseOfShareCapitalType.shareIssue: {
        return this.activeSubscribersForIssue;
      }
      default:
        //Handles increase nominal value
        return this.properties.subscribers;
    }
  }

  // Receives only the subscribers who is a part of the issue and filtered by the selected share types
  get activeSubscribersForIssue(): SubscriberForIssueModel[] {
    const selectedShareTypeIds = this.shareIssue.selectedShareTypeIds;
    return this.subscribersForIssueCandidates.filter(
      (s) => s.getNumberOfSharesForShareTypes(selectedShareTypeIds) > 0,
    );
  }

  // Receives an unfiltered list of all subscribers added in the project
  get subscribersForIssueCandidates(): SubscriberForIssueModel[] {
    return this.isPreferentialRight
      ? this.shareIssue.preferentialIssueSubscribers
      : this.shareIssue.subscribers;
  }

  get totalContributionForIssue() {
    const selectedShareTypeIds = this.shareIssue.selectedShareTypeIds;
    return this.activeSubscribersForIssue
      .reduce(
        (sum, subscriber) => sum.plus(subscriber.getTotalPriceForShareTypes(selectedShareTypeIds)),
        Big(0),
      )
      .toNumber();
  }

  calculateIssuedSharesForActiveSubscribers(shareType: ShareTypeOption) {
    return this.activeSubscribersForIssue
      .reduce(
        (sum, subscriber) =>
          sum.plus(subscriber.getNumberOfSharesForShareType(shareType.sharetypeId)),
        Big(0),
      )
      .toNumber();
  }

  isSharesIssuedForSelectedShareTypes() {
    return this.shareIssue.selectedShareTypes.selected.every(
      (shareType) =>
        this.calculateIssuedSharesForActiveSubscribers(shareType) <=
          shareType.model.maxNumberOfShares &&
        this.calculateIssuedSharesForActiveSubscribers(shareType) >=
          shareType.model.minNumberOfShares,
    );
  }

  get isActiveSubscribersValid(): boolean {
    const isActiveSubscribersValid =
      this.activeSubscribers.length > 0 && !this.activeSubscribers.some((d) => !d.isValid);
    const isShareIssueValid =
      this.showShareIssue && this.isSharesIssuedForSelectedShareTypes() && isActiveSubscribersValid;
    const isIncreaseNominalValueValid = !this.showShareIssue && isActiveSubscribersValid;

    return isShareIssueValid || isIncreaseNominalValueValid;
  }

  toggleContributionInKind(modelIndex: number) {
    this.activeSubscribers[modelIndex].hasContributionInKind =
      !this.activeSubscribers[modelIndex].hasContributionInKind;
  }

  toggleDebtContribution(modelIndex: number) {
    this.activeSubscribers[modelIndex].hasDebtContribution =
      !this.activeSubscribers[modelIndex].hasDebtContribution;
  }

  get selectedExcessAmountMethod(): ExcessAmountMethodOption | undefined {
    return this.excessAmountMethodOptions.find(
      (option) => option.value === this.excessAmountMethod,
    );
  }

  setExcessAmountMethod(option: ExcessAmountMethodOption) {
    if (option.value === "discharged") {
      this.excessAmountMethod = ExcessAmountMethod.discharged;
    }
    if (option.value === "remains_liability") {
      this.excessAmountMethod = ExcessAmountMethod.remains_liability;
    }
    if (option.value === "paidout") {
      this.excessAmountMethod = ExcessAmountMethod.paidout;
    }
  }

  setUsePowerOfAttorney(value: boolean) {
    this.usePowerOfAttorney.setValue(value);
    if (this.usePowerOfAttorney.value === false) {
      this.dispatch(addGeneralMeetingDocuments());
    } else {
      this.isSubscriptionForm.setValue(true);
      this.dispatch(addSubscriptionFormDocument());
    }
  }

  setIsSubscriptionForm(value: boolean) {
    this.isSubscriptionForm.setValue(value);
    if (this.isSubscriptionForm.value) {
      this.dispatch(addSubscriptionFormDocument());
    }
  }

  changeShareCapitalIncreaseType(value?: IncreaseOfShareCapitalType) {
    if (value === "shareIssue") {
      this.properties = this.shareIssue;
    } else {
      this.properties = this.increaseNominalValue;
    }
  }

  get showPowerOfAttorney() {
    return this.usePowerOfAttorney.value === true;
  }

  get showShareIssueOrIncreaseNominalValue() {
    return (
      this.usePowerOfAttorney.value === false ||
      (this.usePowerOfAttorney.value === true &&
        !this.powerOfAttorneyDate.isEmpty &&
        !this.powerOfAttorneyText.isEmpty)
    );
  }

  get hasChosenIncreaseMethod() {
    return this.contributionTypes.selected.length > 0;
  }

  get showShareIssue() {
    return this.hasChosenIncreaseMethod && this.properties.type === "shareIssue";
  }

  get showIncreaseNominalValue() {
    return this.hasChosenIncreaseMethod && this.properties.type === "increaseNominalValue";
  }

  get isPreferentialRight() {
    return this.containsCash && this.shareIssue.preferentialRight.value === true;
  }

  get isExternalSubscribers() {
    return this.shareIssue.preferentialRight.value === false;
    // Not making any sense having external subscribers since not having allocation functionality.
    // Should add, and support, this once allocation is available.
    /*
    return (
      this.shareIssue.preferentialRight.value === false ||
      (this.isPreferentialRight && this.shareIssue.externalSubscribers.value === true)
    );
    */
  }

  get showPreferentialRightWaivedReason() {
    return this.shareIssue.preferentialRight.value === false;
  }

  get showIncreaseContributionPerShare() {
    return (
      this.isIncreaseNominalValueFilled &&
      this.isNewNominalValueValid &&
      this.isNewShareCapitalValid
    );
  }

  get isNewNominalValueValid() {
    const newNominalValue = getNewNominalValue(
      this.currentNominalValue,
      this.increaseNominalValue.increaseInNominalValue.toNumber() || 0,
    );
    return (
      !this.isIncreaseNominalValueFilled ||
      (this.increaseNominalValue.increaseInNominalValue.toNumberOrZero() > 0 &&
        isNominalValueValid(newNominalValue))
    );
  }

  get isIncreaseNominalValueFilled() {
    return this.increaseNominalValue.increaseInNominalValue.parsedValue !== undefined;
  }

  get isNewShareCapitalValid() {
    return isShareCapitalValid(
      this.currentNumberOfShares,
      getNewNominalValue(
        this.currentNominalValue,
        this.increaseNominalValue.increaseInNominalValue.toNumber() || 0,
      ),
    );
  }

  get showContributionInKind() {
    const shareIssueOk =
      this.properties.type !== "shareIssue" ||
      (this.showPreferentialRights && this.isPreferentialRight) ||
      (this.showSubscribers && this.isActiveSubscribersValid);
    return shareIssueOk && this.contributionTypes.selected.includes(ContributionType.kind);
  }

  get showDebtConversion() {
    const shareIssueOk =
      this.properties.type !== "shareIssue" ||
      (this.showPreferentialRights && this.isPreferentialRight) ||
      (this.showSubscribers && this.isActiveSubscribersValid);
    return shareIssueOk && this.contributionTypes.selected.includes(ContributionType.debt);
  }

  get hasSelectedShareTypes(): boolean {
    return (
      this.shareIssue.selectedShareTypes.selected.length > 0 &&
      !this.shareIssue.isSelectedShareTypesMissingValues
    );
  }

  get containsCash(): boolean {
    return this.contributionTypes.selected.includes(ContributionType.cash);
  }

  get isCashOnly(): boolean {
    return this.contributionTypes.selected.length === 1 && this.containsCash;
  }

  get showSubscribers(): boolean {
    return this.showShareIssue && this.hasSelectedShareTypes && !this.isPreferentialRight;
  }

  get showSubscriptionFormForShareIssue() {
    const isForCash =
      this.contributionTypes.isSelected(ContributionType.cash) &&
      this.shareIssue.preferentialRight.value === true &&
      this.shareIssue.preferentialRightTransferable.value !== undefined &&
      this.shareIssue.preferentialRightOversubscribe.value !== undefined &&
      this.shareIssue.preferentialBoardRightsToSellSubscription.value !== undefined;
    const isForDebt = this.contributionTypes.isSelected(ContributionType.debt);
    const isForKind = this.contributionTypes.isSelected(ContributionType.kind);
    // Not making any sense having external subscribers since not having allocation functionality.
    // Should add, and support, this once allocation is available.
    /*
    const hasSubscribers =
      this.shareIssue.externalSubscribers.value === false || this.shareIssue.subscribers.length > 0;
    */
    return this.hasSelectedShareTypes && (isForCash || isForDebt || isForKind);
  }

  get showSubscriptionForm() {
    const noRightsIssue =
      this.shareIssue.preferentialRight.value === false &&
      this.shareIssue.preferentialRightWaivedReasonModel.value !== undefined &&
      this.isActiveSubscribersValid;

    const forIncreaseNominalValue =
      this.properties.type === "increaseNominalValue" &&
      this.isIncreaseNominalValueFilled &&
      this.isNewNominalValueValid &&
      this.isNewShareCapitalValid;
    return this.showSubscriptionFormForShareIssue || noRightsIssue || forIncreaseNominalValue;
  }

  get showIsSubscribersSigning() {
    return this.isSubscriptionForm.value === false;
  }

  get showSignersForAllSubscribers() {
    return this.isOtherSigningForAllSubscribers.value === true;
  }

  get showDeadlineForSubscription() {
    return (
      this.isSubscriptionInMinutesValid ||
      (this.isSubscriptionForm.value === true &&
        !this.periodForSubscription.from.isEmpty &&
        !this.periodForSubscription.to.isEmpty)
    );
  }

  get deadlineForSubscriptionNotEmpty() {
    return !this.deadlineForSubscription.isEmpty;
  }

  get isUpdateArticlesAutomatically() {
    return this.updateArticlesAutomatically.value === true;
  }

  get isUpdateArticlesManually() {
    return this.updateArticlesAutomatically.value === false;
  }

  get showAccountInformation() {
    return this.containsCash;
  }

  get showShareholderRights() {
    // Shareholder rights question is displayed where there is:
    // - share issue
    // - the deadline for subscription is given
    return this.properties.type === IncreaseOfShareCapitalType.shareIssue;
  }

  get showGuaranteeConsortium(): boolean {
    return this.containsCash && this.showShareIssue;
  }

  get showShareIssueNominalValue() {
    return this.currentNumberOfShares === 0;
  }

  get showPreferentialRights() {
    return this.containsCash && this.hasSelectedShareTypes;
  }

  get showSubscribersStatus() {
    return this.isActiveSubscribersValid && this.showPreferentialRights && this.isPreferentialRight;
  }

  get isProjectLocked(): boolean {
    return this.context.isLocked;
  }

  get isArticleChangesValid(): boolean {
    const isAutomaticArticleChangesValid =
      this.isUpdateArticlesAutomatically && this.articleOfAssociationNumber.isValid;
    const isManualArticleChangesValid =
      this.isUpdateArticlesManually && this.articleChangeModel.isValid;
    return isAutomaticArticleChangesValid || isManualArticleChangesValid;
  }

  get isSignersForAllSubscribersEmpty(): boolean {
    return this.signersForAllSubscribers.length === 0;
  }

  get isSubscriptionInMinutesValid(): boolean {
    const isSubscribersSigningMinutes = this.isOtherSigningForAllSubscribers.value === false;
    const isOthersSigningMinutes =
      this.isOtherSigningForAllSubscribers.value === true && !this.isSignersForAllSubscribersEmpty;
    return (
      this.isSubscriptionForm.value === false &&
      (isSubscribersSigningMinutes || isOthersSigningMinutes)
    );
  }

  get isOtherSigningForAllSubscribersValid(): boolean {
    return this.isSubscriptionForm.value === true || this.isSubscriptionInMinutesValid;
  }

  get isValid() {
    return (
      this.contributionTypes.isValid &&
      this.properties.isValid &&
      this.isActiveSubscribersValid &&
      this.isSubscriptionForm.isValid &&
      this.isOtherSigningForAllSubscribersValid &&
      this.periodForSubscription.isValid &&
      this.deadlineForSubscription.isValid &&
      this.isDisposableAccount.isValid &&
      this.isAccountGiven.isValid &&
      this.accountNumber.isValid &&
      this.usePowerOfAttorney.isValid &&
      this.powerOfAttorneyDate.isValid &&
      this.powerOfAttorneyText.isValid &&
      this.estimatedCosts.isValid &&
      this.specialCircumstancesUponSubscriptionModel.value !== undefined &&
      this.specialCircumstancesUponSubscriptionTexts.every((t) => t.model.isValid) &&
      this.specialCircumstancesAfterLastApprovedAnnualAccountModel.value !== undefined &&
      this.specialCircumstancesAfterLastApprovedAnnualAccountTexts.every((t) => t.model.isValid) &&
      this.updateArticlesAutomatically.isValid &&
      this.isArticleChangesValid
    );
  }

  validateActiveSubscribers() {
    this.activeSubscribers.forEach((d) => d.validate());
  }

  validate(): boolean {
    this.contributionTypes.validate();
    this.properties.validate();
    this.validateActiveSubscribers();
    this.isSubscriptionForm.validate();
    this.periodForSubscription.validate();
    this.deadlineForSubscription.validate();
    this.isDisposableAccount.validate();
    this.isAccountGiven.validate();
    this.accountNumber.validate();
    this.usePowerOfAttorney.validate();
    this.powerOfAttorneyDate.validate();
    this.powerOfAttorneyText.validate();
    this.estimatedCosts.validate();
    this.specialCircumstancesUponSubscriptionModel.validate();
    this.specialCircumstancesUponSubscriptionTexts.forEach((t) => t.model.validate());
    this.specialCircumstancesAfterLastApprovedAnnualAccountModel.validate();
    this.specialCircumstancesAfterLastApprovedAnnualAccountTexts.forEach((t) => t.model.validate());
    this.updateArticlesAutomatically.validate();
    this.articleChangeModel.validate();
    return this.isValid;
  }

  get isQuickSetupApplied(): boolean {
    return this.quickSetupType !== undefined;
  }

  setQuickSetup(type?: QuickSetupType) {
    this.quickSetupType = type;
    this.dispatch({
      type: "show-alert",
      message: quickSetupSuccessMessage(type),
      messageType: "success",
    });
    if (type !== undefined) {
      this.autofillDecision();
    }
  }

  openQuickSetupDialog() {
    if (this.quickSetupType !== undefined) {
      this.quickSetupModel = new QuickSetupModel(this.quickSetupType);
    }
  }

  closeQuickSetupDialog() {
    this.quickSetupModel = undefined;
  }

  get isQuickSetupValid() {
    switch (this.quickSetupType) {
      case QuickSetupType.CASH_PREFERENTIAL_RIGHTS:
        return (
          this.showShareIssue &&
          this.isPreferentialRight &&
          this.shareIssue.selectedShareTypes.selected.every((a) => a.model.showShareIssueFixed)
        );
      case QuickSetupType.CASH_PRIVATE_PLACEMENT:
        return (
          this.showShareIssue &&
          this.containsCash &&
          this.isExternalSubscribers &&
          this.shareIssue.selectedShareTypes.selected.every((a) => a.model.showShareIssueFixed)
        );
    }
  }

  autofillDecision() {
    this.setUsePowerOfAttorney(false);
    this.contributionTypes.select(ContributionType.cash);
    this.changeShareCapitalIncreaseType(IncreaseOfShareCapitalType.shareIssue);
    if (this.shareIssue.selectedShareTypes.isNoneSelected) {
      if (this.shareIssue.selectedShareTypes.options.length > 0) {
        this.shareIssue.selectedShareTypes.toggle(this.shareIssue.selectedShareTypes.options[0]);
      } else {
        this.shareIssue.selectedShareTypes.toggleAll(true);
      }
      this.shareIssue.selectedShareTypes.selected.forEach((d) => {
        d.model.sharePrice.setAndCommitInputValue(this.currentNominalValue);
        d.model.changeIssueMode("fixed");
        const numberOfShares = Big(10000000).div(this.currentNominalValue).toNumber();
        d.model.fixed.numberOfShares.setAndCommitInputValue(numberOfShares);
      });
    }
    this.shareIssue.preferentialRight.setValue(
      this.quickSetupType === QuickSetupType.CASH_PREFERENTIAL_RIGHTS,
    );
    if (this.quickSetupType === QuickSetupType.CASH_PREFERENTIAL_RIGHTS) {
      this.shareIssue.preferentialRightTransferable.setValue(false);
      this.shareIssue.preferentialRightOversubscribe.setValue(false);
      this.shareIssue.preferentialBoardRightsToSellSubscription.setValue(false);
      this.shareIssue.externalSubscribers.setValue(false);
    } else {
      this.shareIssue.preferentialRightWaivedReasonModel.setValue(
        PreferentialRightWaivedType.SHAREHOLDERS_IN_UNDERSTANDING,
      );
    }
    this.shareIssue.setSubscribersForIssue();
    this.isSubscriptionForm.setValue(false);
    this.deadlineForSubscription.setAndCommitInputValue(
      toLocalDateISOString(plusDays(today(), 14)),
    );
    this.isDisposableAccount.setValue(true);
    this.updateArticlesAutomatically.setValue(true);
  }
}

function createTextModels(data?: LocalizedText[]): LocalizedStringFieldModel[] {
  return (
    data?.map(
      ({ language, text }) =>
        new LocalizedStringFieldModel(new StringFieldModel({ value: text }), language),
    ) ?? []
  );
}

function updateTextModels(
  languages: Language[],
  models?: LocalizedStringFieldModel[],
): LocalizedStringFieldModel[] {
  return languages.map((lang) => {
    const localized = models?.find((t) => t.locale === lang);
    if (localized) {
      return localized;
    }
    return new LocalizedStringFieldModel(new StringFieldModel(), lang);
  });
}

function asShareIssue(data: ShareCapitalIncreaseType | undefined): ShareIssue | undefined {
  if (data?.type === "shareIssue") {
    return data;
  }
}

function asIncreaseNominalValue(
  data: ShareCapitalIncreaseType | undefined,
): IncreaseNominalValue | undefined {
  if (data?.type === "increaseNominalValue") {
    return data;
  }
}

export type ShareCapitalIncreaseTypeModel = ShareIssueModel | IncreaseNominalValueModel;

export class IncreaseNominalValueModel {
  type: IncreaseOfShareCapitalType.increaseNominalValue;
  increaseInNominalValue: NumberFieldModel;
  sharePrice: NumberFieldModel;
  contributionPerShareTypes: Array<ContributionPerShareTypeModel>;
  subscribers: SubscriberForIncreaseNominalValueModel[];
  inputState?: InputState;

  constructor(
    private context: DecisionContext,
    data?: IncreaseNominalValue,
  ) {
    this.type = IncreaseOfShareCapitalType.increaseNominalValue;
    this.inputState = context.inputState;
    this.increaseInNominalValue = new NumberFieldModel({
      value: data?.increaseInNominalValue,
      required: true,
      isInteger: false,
      isPositive: true,
    });
    this.sharePrice = new NumberFieldModel({ value: data?.sharePrice, isPositiveOrZero: true });
    this.contributionPerShareTypes = this.createContributionPerShareTypeModel(
      data?.contributionPerShareTypes,
    );

    this.subscribers = this.toSubscriberForIncreaseModel(
      context.shareholderData.shareholders.filter((s) =>
        shareholderHasShares(s, this.inputState?.sharesForShareholders),
      ),
      data?.subscribers ?? [],
    );
    makeAutoObservable(this);
  }

  get numberOfSharesForShareType() {
    return ensureShareTypeCodeInSharesForShareTypes(
      this.context.shareholderData.shareTypes,
      this.inputState?.numberOfShares,
    );
  }

  createContributionPerShareTypeModel(contributionPerShareTypes?: ContributionPerShareType[]) {
    return this.numberOfSharesForShareType
      .filter((d) => d.shareTypeCode !== undefined && d.numberOfShares > 0)
      .map((s) => {
        return {
          contributionPerShare:
            contributionPerShareTypes?.find((i) => i.sharetypeId === s.shareTypeId)
              ?.contributionPerShare ?? undefined,
          name: s.shareTypeCode!,
          sharetypeId: s.shareTypeId,
        };
      })
      .map((s) => new ContributionPerShareTypeModel(s, this.context, this.increaseInNominalValue));
  }

  onInputStateChange(inputState?: InputState) {
    const isNumberOfSharesChanged = !isEqual(
      this.inputState?.numberOfShares,
      inputState?.numberOfShares,
    );
    this.inputState = inputState;
    if (isNumberOfSharesChanged) {
      this.contributionPerShareTypes = this.createContributionPerShareTypeModel(
        this.contributionPerShareTypes.map((s) => s.toModel()),
      );
    }
  }

  onLanguageUpdate(languages: Language[]) {
    this.subscribers.map((d) => d.onLanguageUpdate(languages));
  }

  setSubscriberModel(subscribers: SubscriberForIncreaseNominalValue[]) {
    this.subscribers = this.toSubscriberForIncreaseModel(
      this.context.shareholderData.shareholders.filter((s) =>
        shareholderHasShares(s, this.inputState?.sharesForShareholders),
      ),
      subscribers,
    );
  }

  toSubscriberForIncreaseModel(
    shareholders: ExtendedShareholderDTO[],
    data: SubscriberForIncreaseNominalValue[],
  ): SubscriberForIncreaseNominalValueModel[] {
    const subscribers = getSubscribersForIncreaseNominalValue(shareholders, data);
    if (!subscribers) {
      return [];
    }
    return subscribers.map(
      (d) =>
        new SubscriberForIncreaseNominalValueModel(
          d,
          this.context.shareholderData,
          this.context.documentLanguages,
          this.inputState,
        ),
    );
  }

  get isValid(): boolean {
    return (
      this.increaseInNominalValue.isValid &&
      this.contributionPerShareTypes.every((contribution) => contribution.isValid)
    );
  }

  validate(): boolean {
    this.increaseInNominalValue.validate();
    this.contributionPerShareTypes.every((contribution) => contribution.validate());
    return this.isValid;
  }
}

export type ShareTypeOption = {
  sharetypeId: number;
  sharetypeCode: ShareTypeCode;
  isNewShareType: boolean;
  model: ShareIssuePerShareTypeModel;
};

export type ExistingShareholder = {
  id: number;
  name: string;
  email?: string;
  isCompany: boolean;
  isSelected: boolean;
  contactName?: string;
  socialSecurityNumber?: string;
  companyNumber?: string;
};

export type Subscription = {
  id: string;
  sharetypeId: number;
  shareTypeCode: ShareTypeCode;
  numberOfShares: number;
  subscriptionPrice: number;
};

export type SubscriberForIssue = {
  id: number;
  subscriberid: SubscriberId;
  name: string;
  email?: string;
  address?: string;
  organisationNumber?: string;
  isExisting: boolean;
  isCompany: boolean;
  contactName?: string;
  cashContribution?: CashContribution;
  debtContribution?: DebtContribution;
  kindContribution?: KindContribution;
  subscriptions: Subscription[];
  signers: SubscriberSigner[];
};

export type SubscriberSigner = {
  id: string;
  name: string;
  email: string;
};

export type SubscriberForCapitalIncrease =
  | SubscriberForIncreaseNominalValueModel
  | SubscriberForIssueModel;

export class SubscriberForIncreaseNominalValueModel {
  subscriberid: SubscriberId;
  data: SubscriberForIncreaseNominalValue;
  debtContributionDescriptionField: StringFieldModel;
  debtContributionMaxAmountToBeConvertedField: NumberFieldModel;
  debtContributionTotalReceivableField: NumberFieldModel;
  kindContributionValueField: NumberFieldModel;
  kindContributionDescriptionField: LocalizedStringFieldModel[];
  address: StringFieldModel;
  shareholderId: number;
  hasContributionInKind: boolean;
  hasDebtContribution: boolean;
  nameField: StringFieldModel;
  signers: SubscriberSignerModel[];
  languages: Language[];

  constructor(
    data: SubscriberForIncreaseNominalValue,
    private readonly shareholderData: ShareholderData,
    languages: Language[],
    readonly inputState?: InputState,
  ) {
    // This is not initialized correctly due to documents being initialized after decisions
    this.languages = languages;

    this.subscriberid = data.subscriberid;
    this.debtContributionDescriptionField = new StringFieldModel();
    this.debtContributionMaxAmountToBeConvertedField = new NumberFieldModel({
      value: data.debtContribution?.maxAmountToBeConverted,
      isPositiveOrZero: true,
    });
    this.debtContributionTotalReceivableField = new NumberFieldModel({
      value: data.debtContribution?.totalAmountReceivable,
      isPositiveOrZero: true,
    });
    this.kindContributionValueField = new NumberFieldModel({
      value: data.kindContribution?.value,
      isPositiveOrZero: true,
    });
    this.kindContributionDescriptionField = this.kindContributionDescriptionField =
      createTextModels(data.kindContribution?.description);
    this.address = new StringFieldModel({ value: data.address });
    this.data = data;
    this.shareholderId = data.shareholderId;
    this.hasContributionInKind = data.kindContribution !== undefined;
    this.hasDebtContribution = data.debtContribution !== undefined;
    this.nameField = new StringFieldModel({ value: data.name });
    this.signers = data.signers.map((s) => new SubscriberSignerModel(s));
    makeAutoObservable(this, {}, { autoBind: true });
  }

  onLanguageUpdate(languages: Language[]) {
    this.languages = languages;
    this.kindContributionDescriptionField = updateTextModels(
      languages,
      this.kindContributionDescriptionField,
    );
  }

  get email(): string {
    return (
      this.shareholderData.shareholders.find((shareholder) => shareholder.id === this.shareholderId)
        ?.email || ""
    );
  }

  get isCompany(): boolean {
    return (
      this.shareholderData.shareholders.find((shareholder) => shareholder.id === this.shareholderId)
        ?.isCompany || false
    );
  }

  get sharesForShareholder() {
    return (
      this.inputState?.sharesForShareholders.filter(
        (shareholder) => shareholder.shareholderId === this.shareholderId,
      ) ?? []
    );
  }

  getCurrentNumberOfSharesForShareType(shareTypeId: number): number {
    return (
      this.sharesForShareholder.find((shareholder) => shareholder.shareTypeId === shareTypeId)
        ?.numberOfShares ?? 0
    );
  }

  get isAmountError(): boolean {
    return (
      this.debtContributionMaxAmountToBeConvertedField.toNumberOrZero() >
      this.debtContributionTotalReceivableField.toNumberOrZero()
    );
  }

  toDebtContribution(): DebtContribution | null {
    if (!this.hasDebtContribution) {
      return null;
    } else {
      return {
        type: ContributionType.debt,
        description: "",
        maxAmountToBeConverted: this.debtContributionMaxAmountToBeConvertedField.toNumber() ?? 0,
        totalAmountReceivable: this.debtContributionTotalReceivableField.toNumber() ?? 0,
      };
    }
  }

  toKindContribution(): KindContribution | undefined {
    if (!this.hasContributionInKind) {
      return undefined;
    } else {
      return {
        type: ContributionType.kind,
        description: this.kindContributionDescriptionField.map((k) => {
          return { text: k.model.inputValue, language: k.locale };
        }),
        value: this.kindContributionValueField.toNumber() ?? 0,
      };
    }
  }

  toCashContribution(): CashContribution | undefined {
    if (!this.hasContributionInKind) {
      return undefined;
    } else {
      return {
        type: ContributionType.cash,
        amount: 0,
      };
    }
  }

  get isSignerSelf() {
    return (
      this.signers.length === 0 ||
      (this.signers.length === 1 &&
        this.signers.some((d) => d.name.validatedValue === this.nameField.validatedValue))
    );
  }

  addSigner(name: string = "", email: string = "") {
    if (this.signers.every((d) => d.validate())) {
      this.signers.push(new SubscriberSignerModel({ id: newUuid(), name: name, email: email }));
    }
  }

  addSubscriberAsSigner() {
    this.addSigner(this.nameField.validatedValue, this.email);
  }

  removeSigner(signer: SubscriberSignerModel) {
    if (this.signers.includes(signer)) {
      this.signers.splice(this.signers.indexOf(signer), 1);
    }
  }

  get isValid(): boolean {
    return !this.nameField.isEmpty;
  }

  validate(): boolean {
    this.nameField.validate();
    return this.isValid;
  }

  toSigner(): Signer {
    return signer({
      id: this.shareholderId,
      reference: toSubscriberIdReference(this.subscriberid),
      name: this.nameField.validatedValue,
      email: this.email,
      signerType: SignerType.SUBSCRIPTION,
    });
  }

  toSigners(): Signer[] {
    return this.signers.length > 0
      ? this.signers.map((d) => d.toSigner(this.subscriberid))
      : [this.toSigner()];
  }
}

export class SubscriberForIssueModel {
  subscriberid: SubscriberId;
  nameField: StringFieldModel;
  email: StringFieldModel;
  organisationNumber: StringFieldModel;
  contactName: StringFieldModel;
  address: StringFieldModel;
  data: SubscriberForIssue;
  debtContributionMaxAmountToBeConvertedField: NumberFieldModel;
  debtContributionTotalReceivableField: NumberFieldModel;
  kindContributionValueField: NumberFieldModel;
  kindContributionDescriptionField: LocalizedStringFieldModel[];
  shareholderId: number;
  isExisting: boolean;
  isCompany: boolean;
  hasContributionInKind: boolean;
  hasDebtContribution: boolean;
  subscriptions: SubscriptionForIssueModel[];
  signers: SubscriberSignerModel[];
  languages: Language[];

  constructor(
    data: SubscriberForIssue,
    private readonly shareholderData: ShareholderData,
    languages: Language[],
    readonly inputState?: InputState,
  ) {
    // This is not initialized correctly due to documents being initialized after decisions
    this.languages = languages;

    this.subscriberid = data.subscriberid;
    this.shareholderId = data.id;
    this.nameField = new StringFieldModel({
      value: data.name,
      required: true,
      validator: data.isCompany ? undefined : validateName,
    });
    this.email = createEmailField({
      value: data.email ?? this.emailFromShareholderData,
      required: !data.isExisting,
    });
    this.organisationNumber = new StringFieldModel({
      value: data.organisationNumber,
      required: data.isCompany && !data.isExisting,
    });
    this.contactName = new StringFieldModel({
      value: data.contactName,
      required: data.isCompany && !data.isExisting,
    });
    this.address = new StringFieldModel({ value: data.address });
    this.debtContributionMaxAmountToBeConvertedField = new NumberFieldModel({
      value: data.debtContribution?.maxAmountToBeConverted,
      isPositiveOrZero: true,
    });
    this.debtContributionTotalReceivableField = new NumberFieldModel({
      value: data.debtContribution?.totalAmountReceivable,
      isPositiveOrZero: true,
    });
    this.kindContributionValueField = new NumberFieldModel({
      value: data.kindContribution?.value,
      isPositiveOrZero: true,
    });
    this.kindContributionDescriptionField = createTextModels(data.kindContribution?.description);
    this.data = data;
    this.isExisting = data.isExisting;
    this.isCompany = data.isCompany;
    this.hasContributionInKind = data.kindContribution !== undefined;
    this.hasDebtContribution = data.debtContribution !== undefined;
    this.subscriptions = data.subscriptions.map((d) => new SubscriptionForIssueModel(d));
    this.signers = data.signers.map((s) => new SubscriberSignerModel(s));
    makeAutoObservable(this, {}, { autoBind: true });
  }

  onLanguageUpdate(languages: Language[]) {
    this.languages = languages;
    this.kindContributionDescriptionField = updateTextModels(
      languages,
      this.kindContributionDescriptionField,
    );
  }

  get emailFromShareholderData(): string {
    return (
      this.shareholderData.shareholders.find((shareholder) => shareholder.id === this.shareholderId)
        ?.email || ""
    );
  }

  get ssnOrCompanyNumber(): string | undefined {
    const shareholder = this.shareholderData.shareholders.find(
      (shareholder) => shareholder.id === this.shareholderId,
    );
    return shareholder?.isCompany
      ? shareholder?.companyNumber
      : shareholder?.personInfo?.socialSecurityNumber;
  }

  get sharesForShareholder() {
    return (
      this.inputState?.sharesForShareholders.filter(
        (shareholder) => shareholder.shareholderId === this.shareholderId,
      ) ?? []
    );
  }

  get hasShareSubscriptions(): boolean {
    return this.subscriptions.some(
      (subscription) => subscription.numberOfShares.toNumberOrZero() > 0,
    );
  }

  get isAmountError(): boolean {
    return (
      this.debtContributionMaxAmountToBeConvertedField.toNumberOrZero() >
      this.debtContributionTotalReceivableField.toNumberOrZero()
    );
  }

  createSubscription(
    shareTypeId: number,
    shareTypeCode: ShareTypeCode,
    sharePrice: number,
  ): SubscriptionForIssueModel {
    const data = {
      id: newUuid(),
      sharetypeId: shareTypeId,
      shareTypeCode: shareTypeCode,
      numberOfShares: 0,
      subscriptionPrice: sharePrice,
    };
    const newSubscription = new SubscriptionForIssueModel(data);
    this.subscriptions = [...this.subscriptions, newSubscription];
    return newSubscription;
  }

  getTotalPriceForShareTypes(selectedShareTypeIds: number[]) {
    return selectedShareTypeIds.reduce((acc, shareTypeId) => {
      return acc + this.getTotalPriceForShareTypeId(shareTypeId);
    }, 0);
  }

  getTotalPriceForShareTypeId(shareTypeId: number) {
    return this.subscriptions.find((s) => s.sharetypeId === shareTypeId)?.totalPrice ?? 0;
  }

  get currentTotalNumberOfShares(): number {
    return this.sharesForShareholder
      .reduce((sum, shareholder) => sum.plus(shareholder.numberOfShares), Big(0))
      .toNumber();
  }

  newTotalNumberOfShares(selectedShareTypeIds: number[]): number {
    return (
      this.currentTotalNumberOfShares + this.getNumberOfSharesForShareTypes(selectedShareTypeIds)
    );
  }

  getNumberOfSharesForShareTypes(selectedShareTypeIds: number[]): number {
    return selectedShareTypeIds
      .reduce(
        (sum, shareTypeId) => sum.plus(this.getNumberOfSharesForShareType(shareTypeId)),
        Big(0),
      )
      .toNumber();
  }

  getNumberOfSharesForShareType(shareTypeId: number) {
    return (
      this.subscriptions
        .find((s) => s.sharetypeId === shareTypeId)
        ?.numberOfShares.toNumberOrZero() ?? 0
    );
  }

  getTotalPriceForShareType(shareTypeCode: ShareTypeCode) {
    return this.subscriptions.find((s) => s.shareTypeCode === shareTypeCode)?.totalPrice ?? 0;
  }

  getSubscriptionForShareType(
    shareTypeId: number,
    shareTypeCode: ShareTypeCode,
    sharePrice: number,
  ): SubscriptionForIssueModel {
    return (
      this.subscriptions.find((s) => s.sharetypeId === shareTypeId) ??
      this.createSubscription(shareTypeId, shareTypeCode, sharePrice)
    );
  }

  getCurrentNumberOfSharesForShareType(shareTypeId: number): number {
    return (
      this.sharesForShareholder.find((d) => d.shareTypeId === shareTypeId)?.numberOfShares ?? 0
    );
  }

  getNewNumberOfSharesForShareType(shareTypeId: number): number {
    const calculatedShareForShareType = this.getCurrentNumberOfSharesForShareType(shareTypeId);
    const issuedShares = this.getNumberOfSharesForShareType(shareTypeId);
    return Big(calculatedShareForShareType).plus(issuedShares).toNumber();
  }

  toDebtContribution(): DebtContribution | undefined {
    if (!this.hasDebtContribution) {
      return undefined;
    } else {
      return {
        type: ContributionType.debt,
        description: "",
        maxAmountToBeConverted: this.debtContributionMaxAmountToBeConvertedField.toNumberOrZero(),
        totalAmountReceivable: this.debtContributionTotalReceivableField.toNumberOrZero(),
      };
    }
  }

  toKindContribution(): KindContribution | undefined {
    if (!this.hasContributionInKind) {
      return undefined;
    } else {
      return {
        type: ContributionType.kind,
        description: this.kindContributionDescriptionField.map((k) => {
          return { text: k.model.validatedValue, language: k.locale };
        }),
        value: this.kindContributionValueField.toNumberOrZero(),
      };
    }
  }

  toCashContribution(): CashContribution | undefined {
    if (!this.hasContributionInKind) {
      return undefined;
    } else {
      return {
        type: ContributionType.cash,
        amount: 0,
      };
    }
  }

  get isSignerSelf() {
    return (
      this.signers.length === 0 ||
      (this.signers.length === 1 &&
        this.signers.some((d) => d.name.validatedValue === this.nameField.validatedValue))
    );
  }

  addSigner(name: string = "", email: string = "") {
    if (this.signers.every((d) => d.validate())) {
      this.signers.push(new SubscriberSignerModel({ id: newUuid(), name: name, email: email }));
    }
  }

  addSubscriberAsSigner() {
    this.addSigner(
      this.isCompany && isNotBlank(this.contactName.validatedValue)
        ? this.contactName.validatedValue
        : this.nameField.validatedValue,
      this.email.validatedValue,
    );
  }

  removeSigner(signer: SubscriberSignerModel) {
    if (this.signers.includes(signer)) {
      this.signers.splice(this.signers.indexOf(signer), 1);
    }
  }

  get isEmpty(): boolean {
    return this.nameField.isEmpty;
  }

  get isValid(): boolean {
    return this.nameField.isValid && this.email.isValid && this.organisationNumber.isValid;
  }

  validate(): boolean {
    this.nameField.validate();
    this.email.validate();
    this.organisationNumber.validate();
    this.subscriptions.forEach((d) => d.validate());
    return this.isValid;
  }

  toSigner(): Signer {
    return signer({
      id: this.shareholderId,
      reference: toSubscriberIdReference(this.subscriberid),
      name:
        this.isCompany && isNotBlank(this.contactName.validatedValue)
          ? this.contactName.validatedValue
          : this.nameField.validatedValue,
      email: this.email.validatedValue,
      signerType: SignerType.SUBSCRIPTION,
    });
  }

  toSigners(): Signer[] {
    return this.signers.length > 0
      ? this.signers.map((d) => d.toSigner(this.subscriberid))
      : [this.toSigner()];
  }
}

export class SubscriptionForIssueModel {
  id: string;
  sharetypeId: number;
  shareTypeCode: ShareTypeCode;
  numberOfShares: NumberFieldModel;
  subscriptionPrice: NumberFieldModel;
  totalSubscriptionPrice: NumberFieldModel;

  constructor(data: Subscription) {
    this.id = data.id;
    this.sharetypeId = data.sharetypeId;
    this.shareTypeCode = data.shareTypeCode;
    this.numberOfShares = new NumberFieldModel({
      value: data.numberOfShares,
      isPositiveOrZero: true,
      isInteger: true,
      onChange: () => this.updateTotalPrice(),
    });
    this.subscriptionPrice = new NumberFieldModel({
      value: data.subscriptionPrice,
      isPositiveOrZero: true,
    });
    this.totalSubscriptionPrice = new NumberFieldModel({
      value: this.totalPrice,
      isPositiveOrZero: true,
      onChange: (value) => this.updateNumberOfShares(value),
    });

    makeAutoObservable(this, {}, { autoBind: true });
  }

  updateNumberOfShares(totalPrice?: number) {
    const numberOfShares =
      totalPrice === undefined
        ? 0
        : Big(totalPrice).div(this.subscriptionPrice.toNumberOrZero()).toNumber();
    this.numberOfShares.setAndCommitInputValue(numberOfShares);
  }
  updateTotalPrice() {
    this.totalSubscriptionPrice.setInputValue(this.totalPrice);
  }

  get totalPrice(): number {
    return Big(this.numberOfShares.toNumberOrZero())
      .times(this.subscriptionPrice.toNumberOrZero())
      .toNumber();
  }

  get isValid(): boolean {
    return this.numberOfShares.isValid && this.subscriptionPrice.isValid;
  }

  validate(): boolean {
    this.numberOfShares.validate();
    this.subscriptionPrice.validate();
    return this.isValid;
  }
}

export class SubscriberSignerModel {
  id: string;
  name: StringFieldModel;
  email: StringFieldModel;

  constructor(data: SubscriberSigner) {
    this.id = data.id;
    this.name = new StringFieldModel({ value: data.name, required: true, validator: validateName });
    this.email = createEmailField({ value: data.email, required: true });
  }

  get isValid(): boolean {
    return this.name.isValid && this.email.isValid;
  }

  validate(): boolean {
    this.name.validate();
    this.email.validate();
    return this.isValid;
  }

  toSubscriberSigner(): SubscriberSigner {
    return {
      id: this.id,
      name: this.name.validatedValue,
      email: this.email.validatedValue ?? "",
    };
  }

  toSigner(subscriberId: SubscriberId): Signer {
    return signer({
      id: this.id,
      reference: toSubscriberIdReference(subscriberId),
      name: this.name.validatedValue,
      email: this.email.validatedValue ?? "",
      signerType: SignerType.SUBSCRIPTION,
    });
  }
}

const preferentialRightWaivedReasonCaptions: { [key in PreferentialRightWaivedType]: TMessage } = {
  [PreferentialRightWaivedType.SHAREHOLDERS_IN_UNDERSTANDING]: {
    key: "decisions.increaseShareCapitalAgainstCash.newShares.preferentialRightDeviationReason.existingShareholdersAgrees",
  },
  [PreferentialRightWaivedType.DEEMED_NECESSARY]: {
    key: "decisions.increaseShareCapitalAgainstCash.newShares.preferentialRightDeviationReason.necessaryToIncreaseCapital",
  },
  [PreferentialRightWaivedType.ADDED_EXPLANATION]: {
    key: "decisions.increaseShareCapitalAgainstCash.newShares.preferentialRightDeviationReason.other",
  },
};

export class ShareIssueModel {
  type: IncreaseOfShareCapitalType.shareIssue;
  nominalValue: NumberFieldModel;
  preferentialRight: BooleanFieldModel;
  preferentialRightTransferable: BooleanFieldModel;
  preferentialRightOversubscribe: BooleanFieldModel;
  preferentialBoardRightsToSellSubscription: BooleanFieldModel;
  externalSubscribers: BooleanFieldModel;
  preferentialRightWaivedReasonText: StringFieldModel;
  shareholderRights: ShareholderRightsModel;
  isGuaranteeConsortiumEstablished: BooleanFieldModel;
  guaranteeConsortiumCommission: GuaranteeConsortiumCommissionModel;

  inputState?: InputState;

  selectedShareTypes: OptionFieldModel<ShareTypeOption>;

  newSharetypeState: "none" | "initial" | "submitting" | "error" = "none";
  newSharetypeCode?: string;
  subscribers: SubscriberForIssueModel[] = []; // rename this to subscriptions?
  preferentialIssueSubscribers: SubscriberForIssueModel[] = [];
  preferentialRightWaivedReasonModel: RadioFieldModel<PreferentialRightWaivedType | undefined>;

  addShareholdersFromActorsModel?: ActorsSelectorModel;
  newSubscriberForIssue?: SubscriberForIssueModel;
  searchCompany?: SearchCompanyModel;

  constructor(
    readonly dispatch: DbotEventDispatcher,
    readonly context: DecisionContext,
    updateRequiredDependencies: () => void,
    public data?: ShareIssue,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.inputState = context.inputState;

    this.type = IncreaseOfShareCapitalType.shareIssue;
    this.nominalValue = new NumberFieldModel({
      value: data?.nominalValue ?? this.inputState?.sharenominalValue,
      isPositive: true,
      onChange: updateRequiredDependencies,
    });
    this.preferentialRight = new BooleanFieldModel({
      value: data?.preferentialRight ?? this.currentNumberOfShares !== 0,
      onChange: updateRequiredDependencies,
    });
    this.preferentialRightTransferable = new BooleanFieldModel({
      value: data?.preferentialRightTransferable,
    });
    this.preferentialRightOversubscribe = new BooleanFieldModel({
      value: data?.preferentialRightOversubscribe,
    });
    this.preferentialBoardRightsToSellSubscription = new BooleanFieldModel({
      value: data?.preferentialBoardRightsToSellSubscription,
    });
    this.externalSubscribers = new BooleanFieldModel({ value: data?.externalSubscribers });
    this.preferentialRightWaivedReasonText = new StringFieldModel({
      value: data?.preferentialRightWaivedReasonText,
    });
    this.shareholderRights = new ShareholderRightsModel({
      value: data?.shareholderRights ?? ShareholderRights.FROM_REGISTRATION_DATE,
      required: true,
    });
    this.isGuaranteeConsortiumEstablished = new BooleanFieldModel({
      value: data?.isGuaranteeConsortiumEstablished ?? false,
      required: true,
      onChange: updateRequiredDependencies,
    });
    this.guaranteeConsortiumCommission = new GuaranteeConsortiumCommissionModel(
      data?.guaranteeConsortiumCommission,
    );

    this.selectedShareTypes = this.createSelectedShareTypeModel(data?.shareIssuePerShareTypes);

    this.preferentialRightWaivedReasonModel = new RadioFieldModel<
      PreferentialRightWaivedType | undefined
    >({
      value: data?.preferentialRightWaivedReason,
      options: Object.values(PreferentialRightWaivedType),
      idFn: identity,
      labelFn: (value) =>
        value ? preferentialRightWaivedReasonCaptions[value] : { translated: "" },
    });

    this.subscribers = this.toSubscriberForIssueModels(data?.subscribers ?? []);
    this.preferentialIssueSubscribers = this.toPreferentialIssueModel(
      data?.preferentialIssueSubscribers ?? [],
    );
  }

  get existingShareholders() {
    return toExistingShareholders(this.context.shareholderData.shareholders);
  }

  get selectedShareTypeIds() {
    return this.selectedShareTypes.selected.map((d) => d.sharetypeId);
  }

  get totalShareTypePrice() {
    return this.selectedShareTypes.selected
      .reduce((sum, shareTypeOption) => sum.plus(shareTypeOption.model.maxTotalPrice), Big(0))
      .toNumber();
  }

  get shareTypeCodes() {
    return this.context.shareholderData.shareTypeCodes.filter(
      (shareTypeCode) =>
        !this.selectedShareTypes.options.some((d) => d.sharetypeCode === shareTypeCode),
    );
  }

  get numberOfSharesForShareType() {
    return ensureShareTypeCodeInSharesForShareTypes(
      this.context.shareholderData.shareTypes,
      this.inputState?.numberOfShares,
    );
  }

  get newNumberOfShares() {
    return this.selectedShareTypes.selected
      .reduce((sum, shareTypeOption) => sum.plus(shareTypeOption.model.maxNumberOfShares), Big(0))
      .plus(this.currentNumberOfShares)
      .toNumber();
  }

  get currentNumberOfShares() {
    return total(this.inputState?.numberOfShares ?? []);
  }

  get currentShareNominalValue() {
    return this.currentNumberOfShares === 0
      ? this.nominalValue.toNumberOrZero()
      : (this.inputState?.sharenominalValue ?? 0);
  }

  createSelectedShareTypeModel(data?: ShareIssuePerShareType[]) {
    const shareIssueData = (id: number) => data?.find((d) => d.sharetypeId === id);

    return ShareIssueModel.createSelectedShareTypes(
      data?.map((s) => ({
        sharetypeId: s.sharetypeId,
        sharetypeCode: s.sharetypeCode,
        isNewShareType: false,
        model: new ShareIssuePerShareTypeModel(
          this.dispatch,
          s.sharetypeId,
          s.sharetypeCode,
          this.currentShareNominalValue,
          this.setSubscribersForIssue,
          s,
        ),
      })),
      this.getSharetypeOptions(shareIssueData, this.dispatch, data),
    );
  }

  onInputStateChange(inputState?: InputState) {
    const isNumberOfSharesChanged = !isEqual(
      this.inputState?.numberOfShares,
      inputState?.numberOfShares,
    );
    this.inputState = inputState;
    if (isNumberOfSharesChanged) {
      this.selectedShareTypes = this.createSelectedShareTypeModel(toShareIssueForShareTypes(this));
    }
    if (this.currentNumberOfShares === 0) {
      this.preferentialRight.setValue(false);
    }
  }

  onLanguageUpdate(languages: Language[]) {
    this.subscribers.map((d) => d.onLanguageUpdate(languages));
    this.preferentialIssueSubscribers.map((d) => d.onLanguageUpdate(languages));
  }

  get shareSumByType(): Record<string, number> {
    return this.numberOfSharesForShareType
      .filter((s) => s.shareTypeCode !== undefined)
      .reduce(
        (pre, type) => ({
          ...pre,
          [type.shareTypeCode!]: type.numberOfShares,
        }),
        {},
      );
  }

  get shareholders(): ExtendedShareholderDTO[] {
    return this.context.shareholderData.shareholders;
  }

  private getSharetypeOptions(
    shareIssueData: (id: number) => ShareIssuePerShareType | undefined,
    dispatch: DbotEventDispatcher,
    shareIssuePerShareTypes?: ShareIssuePerShareType[],
  ): ShareTypeOption[] {
    const shareTypeOptions: ShareTypeOption[] = this.numberOfSharesForShareType
      .filter((s) => s.shareTypeCode !== undefined)
      ?.map((s) => ({
        sharetypeId: s.shareTypeId,
        sharetypeCode: s.shareTypeCode!,
        isNewShareType: shareIssueData(s.shareTypeId)?.isNewShareType || false,
        model: new ShareIssuePerShareTypeModel(
          dispatch,
          s.shareTypeId,
          s.shareTypeCode!,
          this.currentShareNominalValue,
          this.setSubscribersForIssue,
          shareIssueData(s.shareTypeId),
        ),
      }));
    const companySharetypeIds = this.numberOfSharesForShareType.map((s) => s.shareTypeId);
    if (shareIssuePerShareTypes !== undefined && shareIssuePerShareTypes.length > 0) {
      const storedShareIssues: ShareTypeOption[] = shareIssuePerShareTypes
        .filter((s) => !companySharetypeIds.includes(s.sharetypeId))
        .map((s) => ({
          sharetypeId: s.sharetypeId,
          sharetypeCode: s.sharetypeCode,
          isNewShareType: s.isNewShareType,
          model: new ShareIssuePerShareTypeModel(
            dispatch,
            s.sharetypeId,
            s.sharetypeCode,
            this.currentShareNominalValue,
            this.setSubscribersForIssue,
            shareIssueData(s.sharetypeId),
          ),
        }));
      return shareTypeOptions.concat(storedShareIssues);
    } else {
      return shareTypeOptions;
    }
  }

  setNewSharetypeCode(value: string) {
    this.newSharetypeCode = value;
  }

  setSubscribersModel(subscribers: SubscriberForIssue[], isPreferentialRight: boolean) {
    if (isPreferentialRight) {
      this.preferentialIssueSubscribers = this.toSubscriberForIssueModels(subscribers);
    } else {
      this.subscribers = this.toSubscriberForIssueModels(subscribers);
    }
  }

  deleteSubscriber(subscriber: SubscriberForIssueModel) {
    this.subscribers = this.subscribers.filter((value) => value !== subscriber);
  }

  openAddNewShareholderFromPortfolioDialog() {
    this.addShareholdersFromActorsModel = openAddNewShareholderFromPortfolioDialog(
      this.context.orgNo,
      this.dispatch,
      this.subscribers,
      this.existingShareholders,
      this.addSubscribers,
      this.closeAddShareholderFromActorsDialog,
    );
  }

  closeAddShareholderFromActorsDialog() {
    this.addShareholdersFromActorsModel = undefined;
  }

  searchCompanies(input: string) {
    this.dispatch({ type: "dbot/search-companies", input, source: this });
  }

  openAddNewShareholderDialog(isCompany: boolean) {
    const newSubscriber = this.createSubscriber({ isCompany });
    this.newSubscriberForIssue = this.toSubscriberForIssueModel(newSubscriber);
    this.searchCompany = new SearchCompanyModel(
      this.searchCompanies,
      this.newSubscriberForIssue.nameField,
      this.newSubscriberForIssue.organisationNumber,
    );
  }

  submitNewShareholderSubscriber() {
    if (this.newSubscriberForIssue !== undefined && this.newSubscriberForIssue.validate()) {
      this.newSubscriberForIssue.addSubscriberAsSigner();
      this.subscribers = this.subscribers.concat(this.newSubscriberForIssue);
      this.closeAddSubscriberDialog();
    }
  }

  closeAddSubscriberDialog() {
    this.newSubscriberForIssue = undefined;
  }

  addSubscribers(newSubscribers: SubscriberForIssue[], shareholders?: ExtendedShareholderDTO[]) {
    const selectedSubscribers = newSubscribers.map((s) => this.createSubscriber(s));
    this.subscribers.push(...this.toSubscriberForIssueModels(selectedSubscribers));
    if (shareholders !== undefined) {
      this.context.shareholderData.shareholders.push(...shareholders);
    }
  }

  createSubscriber(data?: Partial<SubscriberForIssue>) {
    return createSubscriberForIssue({
      ...data,
      subscriptions:
        data?.subscriptions ??
        this.selectedShareTypes.selected.map<Subscription>((s) => ({
          id: newUuid(),
          sharetypeId: s.sharetypeId,
          shareTypeCode: s.sharetypeCode,
          numberOfShares: 0,
          subscriptionPrice: s.model.sharePrice.toNumberOrZero(),
        })),
    });
  }

  toSubscriberForIssueModelWithSelectedShareTypes(
    data: SubscriberForIssue[],
  ): SubscriberForIssueModel[] {
    const subscribers = data.map((subscriber) => {
      return {
        ...subscriber,
        subscriptions: subscriber.subscriptions.map((subscription) => {
          return {
            ...subscription,
            subscriptionPrice:
              this.selectedShareTypes.selected
                .find((d) => d.sharetypeId === subscription.sharetypeId)
                ?.model.sharePrice.toNumberOrZero() ?? subscription.subscriptionPrice,
          };
        }),
      };
    });
    return this.toSubscriberForIssueModels(subscribers);
  }

  toSubscriberForIssueModels(data: SubscriberForIssue[]): SubscriberForIssueModel[] {
    return data.map((d) => this.toSubscriberForIssueModel(d));
  }

  toSubscriberForIssueModel(data: SubscriberForIssue): SubscriberForIssueModel {
    return new SubscriberForIssueModel(
      data,
      this.context.shareholderData,
      this.context.documentLanguages,
      this.inputState,
    );
  }

  setSubscribersForIssue() {
    this.preferentialIssueSubscribers = this.toPreferentialIssueModel(
      this.preferentialIssueSubscribers.map((d) => toSubscriberForIssue(d)),
    );
    this.subscribers = this.toSubscriberForIssueModelWithSelectedShareTypes(
      this.subscribers.map((d) => toSubscriberForIssue(d)),
    );
  }

  toPreferentialIssueModel(data: SubscriberForIssue[]): SubscriberForIssueModel[] {
    const subscribers = subscribersForIssueFromProRataCalculation(
      data,
      this.selectedShareTypes.selected,
      this.currentNumberOfShares,
      this.shareholders,
      this.inputState?.sharesForShareholders ?? [],
    );
    return this.toSubscriberForIssueModels(subscribers);
  }

  get isAddNewSharetype(): boolean {
    return this.newSharetypeState !== "none";
  }

  get isValidNewSharetypeCode(): boolean {
    return (
      this.newSharetypeState === "initial" ||
      (this.newSharetypeCode !== undefined && this.newSharetypeCode.trim().length > 0)
    );
  }

  submitNewShareType() {
    this.newSharetypeState = "submitting";
    if (this.isValidNewSharetypeCode) {
      this.addNewSharetype();
      this.clearNewShareType();
    }
  }

  clearNewShareType() {
    this.newSharetypeCode = undefined;
    this.newSharetypeState = "none";
  }

  hasContributionInKind(s: SubscriberForIssue) {
    return s && s.kindContribution !== undefined;
  }

  private static createSelectedShareTypes(
    selected: ShareTypeOption[] | undefined,
    options: ShareTypeOption[],
  ) {
    return new OptionFieldModel({
      value: selected,
      required: true,
      options: options,
      idFn: (v) => v.sharetypeId.toString(),
      labelFn: (o) =>
        o.isNewShareType
          ? {
              key: "dbot.newShareType",
              params: { shareTypeLabel: shareTypeLabel(o.sharetypeCode) },
            }
          : shareTypeLabel(o.sharetypeCode),
    });
  }

  toggleSelectedShareType(option: ShareTypeOption) {
    this.selectedShareTypes.toggle(option);
    this.setSubscribersForIssue();
  }

  openAddNewSharetype() {
    this.newSharetypeState = "initial";
  }

  private addNewSharetype() {
    if (!this.newSharetypeCode || !(this.newSharetypeCode in ShareTypeCode)) {
      return;
    }
    this.newSharetypeState = "submitting";
    const newSharetypeId = -new Date().valueOf();
    const newsharetype: ShareTypeOption = {
      sharetypeId: newSharetypeId,
      sharetypeCode: this.newSharetypeCode as ShareTypeCode,
      isNewShareType: true,
      model: new ShareIssuePerShareTypeModel(
        this.dispatch,
        newSharetypeId,
        this.newSharetypeCode,
        this.currentShareNominalValue,
        this.setSubscribersForIssue,
      ),
    };
    const selected = [...this.selectedShareTypes.selected, newsharetype];
    const options = [...this.selectedShareTypes.options, newsharetype];
    this.selectedShareTypes = ShareIssueModel.createSelectedShareTypes(selected, options);
  }

  get isProjectLocked(): boolean {
    return this.context.isLocked;
  }

  get isSelectedShareTypesMissingValues() {
    return (
      !this.selectedShareTypes.isValid ||
      this.selectedShareTypes.selected.some((d) => d.model.isMissingValues)
    );
  }

  get isSelectedShareTypesValid() {
    return (
      this.selectedShareTypes.isValid &&
      this.selectedShareTypes.selected.every((d) => d.model.isValid)
    );
  }

  get isValid(): boolean {
    return (
      this.nominalValue.isValid &&
      this.isSelectedShareTypesValid &&
      this.preferentialRight.isValid &&
      this.preferentialRightTransferable.isValid &&
      this.preferentialRightOversubscribe.isValid &&
      this.preferentialBoardRightsToSellSubscription.isValid &&
      this.externalSubscribers.isValid &&
      this.preferentialRightWaivedReasonModel.isValid &&
      this.shareholderRights.isValid &&
      this.isGuaranteeConsortiumEstablished.isValid &&
      this.guaranteeConsortiumCommission.isValid
    );
  }

  validateSelectedShareTypes(): boolean {
    this.selectedShareTypes.validate();
    this.selectedShareTypes.selected.every((d) => d.model.validate());
    return this.isSelectedShareTypesValid;
  }

  validate(): boolean {
    this.nominalValue.validate();
    this.validateSelectedShareTypes();
    this.preferentialRight.validate();
    this.preferentialRightTransferable.validate();
    this.preferentialRightOversubscribe.validate();
    this.preferentialBoardRightsToSellSubscription.validate();
    this.externalSubscribers.validate();
    this.preferentialRightWaivedReasonModel.validate();
    this.shareholderRights.validate();
    this.isGuaranteeConsortiumEstablished.validate();
    this.guaranteeConsortiumCommission.validate();
    return this.isValid;
  }
}

export class ContributionPerShareTypeModel {
  contributionPerShare: NumberFieldModel;
  readonly data: ContributionPerShareType;

  constructor(
    data: ContributionPerShareType,
    private context: DecisionContext,
    readonly increaseInNominalValue: NumberFieldModel,
  ) {
    this.data = data;
    this.contributionPerShare = new NumberFieldModel({
      value: data?.contributionPerShare,
      isPositiveOrZero: true,
      required: true,
      validator: (value) => {
        return value !== undefined && this.increaseInNominalValue.toNumberOrZero() > value
          ? {
              key: "decisions.increaseShareCapitalAgainstCash.increaseNominalValue.shareContribution-helperText",
              params: {
                currency: CURRENCY_NOK,
                newShareNominalValue: this.increaseInNominalValue.toNumberOrZero(),
              },
            }
          : undefined;
      },
    });
    makeAutoObservable(this);
  }

  get sharetypeLabel(): TMessage {
    return shareTypeLabel(this.data.name);
  }

  get isValid(): boolean {
    return this.contributionPerShare.isValid;
  }

  validate(): boolean {
    return this.contributionPerShare.validate();
  }

  get isProjectLocked(): boolean {
    return this.context.isLocked;
  }

  toModel(): ContributionPerShareType {
    return {
      contributionPerShare: this.contributionPerShare.toNumber(),
      sharetypeId: this.data.sharetypeId,
      name: this.data.name,
    };
  }
}

export class ShareIssuePerShareTypeModel {
  fixed: FixedModel;
  interval: IntervalModel;
  issueMode: ShareIssueModeModel;
  sharePrice: NumberFieldModel;
  isSelected: boolean;

  constructor(
    readonly dispatch: DbotEventDispatcher,
    public readonly sharetypeId: number,
    public readonly sharetypeCode: string,
    public readonly shareNominalValue: number,
    public setSubscribersForIssue: () => void,
    public data?: ShareIssuePerShareType,
  ) {
    this.fixed = new FixedModel(asFixed(data?.issueMode));
    this.interval = new IntervalModel(asInterval(data?.issueMode));
    this.issueMode = data?.issueMode.type === "interval" ? this.interval : this.fixed;
    this.sharePrice = new NumberFieldModel({
      value: data?.sharePrice ?? shareNominalValue,
      isPositiveOrZero: true,
      required: true,
      validator: (value) => {
        return value !== undefined && this.shareNominalValue > value
          ? {
              key: "decisions.increaseShareCapitalAgainstCash.newShares.subscriptionValue.helperText",
              params: {
                currency: CURRENCY_NOK,
                shareNominalValue: this.shareNominalValue,
              },
            }
          : undefined;
      },
    });
    this.isSelected = data !== undefined;
    makeAutoObservable(this);
  }

  changeIssueMode(type: string) {
    if (type === "interval") {
      this.issueMode = this.interval;
    } else {
      this.issueMode = this.fixed;
    }
    this.setSubscribersForIssue();
  }

  get maxTotalPrice(): number {
    return Big(this.maxNumberOfShares).mul(this.sharePrice.toNumberOrZero()).toNumber();
  }

  get minNumberOfShares(): number {
    switch (this.issueMode.type) {
      case "fixed":
        return this.issueMode.numberOfShares.toNumberOrZero();
      case "interval":
        return this.issueMode.from.toNumberOrZero();
    }
  }

  get maxNumberOfShares(): number {
    switch (this.issueMode.type) {
      case "fixed":
        return this.issueMode.numberOfShares.toNumberOrZero();
      case "interval":
        return this.issueMode.to.toNumberOrZero();
    }
  }

  get showShareIssueFixed() {
    return this.issueMode.type === "fixed";
  }

  get showShareIssueInterval() {
    return this.issueMode.type === "interval";
  }

  get isMissingValues(): boolean {
    return this.issueMode.isMissingValues || this.sharePrice.toNumber() === undefined;
  }

  get isValid(): boolean {
    return this.issueMode.isValid && this.sharePrice.isValid;
  }

  validate(): boolean {
    this.issueMode.validate();
    this.sharePrice.validate();
    return this.isValid;
  }
}

function asFixed(data: ShareIssueMode | undefined): Fixed | undefined {
  if (data?.type === "fixed") {
    return data;
  }
}

function asInterval(data: ShareIssueMode | undefined): Interval | undefined {
  if (data?.type === "interval") {
    return data;
  }
}

type ShareIssueModeModel = FixedModel | IntervalModel;

class FixedModel {
  type: "fixed";
  numberOfShares: NumberFieldModel;

  constructor(data?: Fixed) {
    this.type = "fixed";
    this.numberOfShares = new NumberFieldModel({
      value: data?.numberOfShares,
      isPositiveOrZero: true,
      required: true,
      isInteger: true,
    });
    makeAutoObservable(this);
  }

  get isMissingValues(): boolean {
    return this.numberOfShares.toNumber() === undefined;
  }

  get isValid(): boolean {
    return this.numberOfShares.isValid;
  }

  validate(): boolean {
    return this.numberOfShares.validate();
  }
}

class IntervalModel {
  type: "interval";
  from: NumberFieldModel;
  to: NumberFieldModel;

  constructor(data?: Interval) {
    this.type = "interval";
    this.from = new NumberFieldModel({
      value: data?.from,
      required: true,
      isInteger: true,
      isPositiveOrZero: true,
    });
    this.to = new NumberFieldModel({
      value: data?.to,
      isPositiveOrZero: true,
      required: true,
      isInteger: true,
    });
    makeAutoObservable(this);
  }

  get isMissingValues(): boolean {
    return this.from.toNumber() === undefined || this.to.toNumber() === undefined;
  }

  get isValid(): boolean {
    return this.from.isValid && this.to.isValid;
  }

  validate(): boolean {
    this.from.validate();
    this.to.validate();
    return this.isValid;
  }
}

class GuaranteeConsortiumCommissionModel {
  numberOfShares: NumberFieldModel;
  percentOfGuaranteedAmount: NumberFieldModel;

  constructor(data?: GuaranteeConsortiumCommission) {
    this.numberOfShares = new NumberFieldModel({
      value: data?.numberOfShares,
      isPositiveOrZero: true,
      isInteger: true,
    });
    this.percentOfGuaranteedAmount = new NumberFieldModel({
      value: data?.percentOfGuaranteedAmount,
      isPositiveOrZero: true,
      isInteger: true,
    });
    makeAutoObservable(this);
  }

  setRequired(isRequired: boolean | undefined) {
    this.numberOfShares.setRequired(isRequired);
    this.percentOfGuaranteedAmount.setRequired(isRequired);
  }

  get isValid(): boolean {
    return this.numberOfShares.isValid && this.percentOfGuaranteedAmount.isValid;
  }

  validate(): boolean {
    return this.numberOfShares.validate() && this.percentOfGuaranteedAmount.validate();
  }
}

export class PeriodForSubscriptionModel {
  from: LocalDateFieldModel;
  to: LocalDateFieldModel;

  constructor(data?: PeriodForSubscription) {
    this.from = new LocalDateFieldModel({ value: data?.from });
    this.to = new LocalDateFieldModel({ value: data?.to });
    makeAutoObservable(this);
  }

  setRequired(isSubscriptionPeriod: boolean | undefined) {
    this.from.setRequired(isSubscriptionPeriod);
    this.to.setRequired(isSubscriptionPeriod);
  }

  get isValid(): boolean {
    return this.from.isValid && this.to.isValid;
  }

  validate(): boolean {
    return this.from.validate() && this.to.validate();
  }
}

export class ShareholderRightsModel {
  readonly params: { value?: ShareholderRights; required?: boolean };

  value?: ShareholderRights;
  error?: TMessage = undefined;

  constructor(params: { value?: ShareholderRights; required?: boolean } = {}) {
    makeAutoObservable(this, { params: false }, { autoBind: true });
    this.params = params;
    this.value = params.value;
  }

  get isRequired(): boolean {
    return Boolean(this.params.required);
  }

  get isValid(): boolean {
    return !this.error;
  }

  validate() {
    if (this.params.required && this.value === undefined) {
      this.error = { key: "required" };
    } else {
      this.error = undefined;
    }
  }

  setValue(value: string) {
    if (value === "FROM_REGISTRATION_DATE") this.value = ShareholderRights.FROM_REGISTRATION_DATE;
    else if (value === "FROM_SUBSCRIPTION_DATE")
      this.value = ShareholderRights.FROM_SUBSCRIPTION_DATE;
  }
}

export type ViewSubscribersDialogModel =
  | { type: "status"; subscriberModel: SubscribersStatusModel }
  | { type: "allocation"; subscriberModel: ShareAllocationRoundsModel };

export class ShareAllocationRoundsModel {
  decisionId: string;
  allocationResponse: Result<ShareAllocationRoundsResponse, TMessage> = pending();
  saveAllocationResult: Result = ok();
  allocationsRounds: ShareAllocationRoundModel[];
  selectedAllocationRound: ShareAllocationRoundModel;
  isAllocationCompleted: boolean;

  constructor(
    decisionId: string,
    readonly dispatch: DbotEventDispatcher,
    readonly shareholders: ExtendedShareholderDTO[],
    readonly closeDialog: () => void,
    readonly inputState?: InputState,
    data?: ShareAllocationRoundsResponse,
  ) {
    makeAutoObservable(this, { dispatch: false }, { autoBind: true });
    this.decisionId = decisionId;
    this.allocationsRounds =
      data?.allocationRounds.map(
        (d) => new ShareAllocationRoundModel(d, this.shareholders, this.inputState),
      ) ?? [];
    this.selectedAllocationRound = this.allocationsRounds[0];
    this.isAllocationCompleted = data?.isAllocationCompleted ?? false;
  }

  init(): void {
    this.dispatch({ type: "dbot/fetch-increase-of-share-capital-subscribers-allocation" });
  }

  handleEvent(event: DbotEvent) {
    switch (event.type) {
      case "dbot/fetch-increase-of-share-capital-subscribers-allocation-result":
        this.toModel(event.result);
        break;
      case "dbot/save-share-allocation-result":
        this.dispatch(
          updateFollowUpStatusEvent(this.decisionId, { type: FollowUpType.allocatedShares }),
        );
        this.saveAllocationResult = ok();
        if (isOk(event.result)) {
          this.isAllocationCompleted = event.result.data;
        }
        break;
    }
  }

  toModel(result: Ok<ShareAllocationRoundsResponse> | Failure<TMessage>) {
    this.allocationResponse = result;
    if (isOk(this.allocationResponse)) {
      this.allocationsRounds = this.allocationResponse.data.allocationRounds.map(
        (d) => new ShareAllocationRoundModel(d, this.shareholders, this.inputState),
      );
      this.isAllocationCompleted = this.allocationResponse.data.isAllocationCompleted;
      this.selectedAllocationRound = this.allocationsRounds[0];
    }
  }

  allocateAllOfferedShares() {
    this.allocationsRounds.map((round) => round.allocateAllOfferedSharesInRound());
  }

  get isAllSharesAllocatedInRounds() {
    return this.allocationsRounds.every((d) => d.isAllSharesAllocated);
  }

  get shareTypeCodes() {
    return Array.from(new Set(this.allocationsRounds.map((d) => d?.shareTypeCode)));
  }

  setSelectedAllocation(value: ShareAllocationRoundModel) {
    this.selectedAllocationRound = value;
  }

  get isEmpty() {
    return this.shareTypeCodes.length === 0 && this.allocationsRounds.length === 0;
  }

  prepareData(): ShareAllocationRound[] {
    return this.allocationsRounds.map((data) => data.prepareData());
  }

  saveAllocation(isCompleted: boolean) {
    this.saveAllocationResult = pending();
    this.dispatch({
      type: "dbot/save-share-allocation",
      allocationRounds: this.prepareData(),
      isAllocationCompleted: isCompleted,
    });
  }

  saveAndClose() {
    if (!this.isAllocationCompleted) this.saveAllocation(this.isAllocationCompleted);
    this.closeDialog();
  }

  close() {
    this.closeDialog();
  }

  get canConfirmAllocation() {
    return this.hasAllocatedValidNumberOfShares && this.isAllSharesAllocatedInRounds;
  }

  get hasAllocatedValidNumberOfShares() {
    return this.allocationsRounds.every((d) => d.remainingSharesToAllocate >= 0);
  }

  toggleAllocationStatusAndSave() {
    this.saveAllocation(!this.isAllocationCompleted);
  }
}

export class ShareAllocationRoundModel {
  id: string;
  projectId: string;
  documentId: string;
  roundNumber: number;
  sharetypeId?: number;
  shareTypeCode: ShareTypeCode;
  minShares: number;
  maxShares: number;
  allocations: ShareAllocationModel[];
  isCompleted: boolean;

  constructor(
    data: ShareAllocationRound,
    readonly shareholders: ExtendedShareholderDTO[],
    readonly inputState?: InputState,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.id = data.id;
    this.projectId = data.projectId;
    this.documentId = data.documentId;
    this.roundNumber = data.roundNumber;
    this.sharetypeId = data.sharetypeId;
    this.shareTypeCode = data.shareTypeCode;
    this.minShares = data.minShares;
    this.maxShares = data.maxShares;
    this.allocations = data.allocations.map((d) => new ShareAllocationModel(d));
    this.isCompleted = data.isCompleted;
  }

  allocateAllOfferedSharesInRound() {
    this.allocations.map((allocation) => {
      allocation.allocatedShares.setAndCommitInputValue(allocation.offeredShares);
    });
  }

  get isAllSharesAllocated() {
    return this.allocations.every((d) => d.isAllocatedShares);
  }

  get isSharesFixed() {
    return this.minShares === this.maxShares;
  }

  currentNumberOfSharesForSubscriber(id?: number) {
    return (
      this.inputState?.sharesForShareholders.find(
        (d) => d.shareholderId === id && d.shareTypeId === this.sharetypeId,
      )?.numberOfShares ?? 0
    );
  }

  newNumberOfSharesForSubscriber(newNumberOfShares: number, id?: number) {
    return this.currentNumberOfSharesForSubscriber(id) + newNumberOfShares;
  }

  get remainingSharesToAllocate() {
    return Big(this.maxShares).minus(this.currentAllocatedShares).toNumber();
  }

  get currentNumberOfShares() {
    return (
      this.inputState?.numberOfShares.find((share) => share.shareTypeId === this.sharetypeId)
        ?.numberOfShares ?? 0
    );
  }

  get currentAllocatedShares() {
    return this.allocations?.reduce(
      (sum, allocation) => sum + allocation?.allocatedShares.toNumberOrZero(),
      0,
    );
  }

  get newTotalNumberOfShares() {
    return this.currentNumberOfShares + this.currentAllocatedShares;
  }

  fetchCurrentOwnership(id?: number) {
    const sharesForShareholder = this.currentNumberOfSharesForSubscriber(id);
    return this.currentNumberOfShares !== 0
      ? Big(sharesForShareholder).div(this.currentNumberOfShares).toNumber()
      : 0;
  }

  fetchNewOwnership(newNumberOfShares: number, id?: number) {
    const sharesForShareholder = this.newNumberOfSharesForSubscriber(newNumberOfShares, id);
    return this.newTotalNumberOfShares !== 0
      ? Big(sharesForShareholder).div(this.newTotalNumberOfShares).toNumber()
      : 0;
  }

  get totalPrice() {
    return this.allocations?.reduce(
      (sum, allocation) =>
        sum +
        Big(allocation.subscriptionPrice)
          .mul(allocation.allocatedShares.toNumberOrZero())
          .toNumber(),
      0,
    );
  }

  prepareData(): ShareAllocationRound {
    return {
      id: this.id,
      projectId: this.projectId,
      documentId: this.documentId,
      sharetypeId: this.sharetypeId,
      shareTypeCode: this.shareTypeCode,
      roundNumber: this.roundNumber,
      minShares: this.minShares,
      maxShares: this.maxShares,
      allocations: this.allocations.map((data) => data.prepareData()),
      isCompleted: this.isCompleted,
    };
  }

  get toTableData(): SubscriberAllocationRow[] {
    return this.allocations.map((subscriber) => {
      return {
        id: subscriber.subscriberId,
        title: subscriber.name,
        offeredShares: subscriber.offeredShares,
        subscribedShares: subscriber.subscribedShares,
        allocatedShares: subscriber.allocatedShares,
        subscriptionPrice: subscriber.subscriptionPrice,
        totalPrice: subscriber.totalPrice,
        currentOwnership: this.fetchCurrentOwnership(subscriber.shareholderId),
        newOwnership: this.fetchNewOwnership(
          subscriber.allocatedShares.toNumberOrZero(),
          subscriber.shareholderId,
        ),
        currentNumberOfShares: this.currentNumberOfSharesForSubscriber(subscriber.shareholderId),
        newNumberOfShares: this.newNumberOfSharesForSubscriber(
          subscriber.allocatedShares.toNumberOrZero(),
          subscriber.shareholderId,
        ),
      };
    });
  }
}

export class ShareAllocationModel {
  id: string;
  subscriberId: SubscriberId;
  shareholderId?: number;
  name: string;
  offeredShares: number;
  subscribedShares?: number;
  allocatedShares: NumberFieldModel;
  subscriptionPrice: number;

  constructor(data: ShareAllocation) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.id = data.id;
    this.subscriberId = data.subscriberId;
    this.shareholderId = data.shareholderId;
    this.name = data.name;
    this.offeredShares = data.offeredShares;
    this.subscribedShares = data.subscribedShares;
    this.allocatedShares = new NumberFieldModel({
      value: data.allocatedShares ?? data.subscribedShares,
      isPositiveOrZero: true,
      isInteger: true,
    });
    this.subscriptionPrice = data.subscriptionPrice;
  }

  get isAllocatedShares() {
    return !this.allocatedShares.isEmpty;
  }

  get totalPrice() {
    return Big(this.subscriptionPrice).mul(this.allocatedShares.toNumberOrZero()).toNumber();
  }

  prepareData(): ShareAllocation {
    return {
      id: this.id,
      subscriberId: this.subscriberId,
      shareholderId: this.shareholderId,
      name: this.name,
      offeredShares: this.offeredShares,
      subscribedShares: this.subscribedShares,
      allocatedShares: this.allocatedShares.toNumber(),
      subscriptionPrice: this.subscriptionPrice,
    };
  }
}

export class SearchCompanyModel {
  searchCompanies: (input: string) => void;
  selectedCompany?: SuggestedCompany;
  companies: SuggestedCompany[] = [];
  companyName: StringFieldModel;
  orgNo: StringFieldModel;

  constructor(
    searchCompanies: (input: string) => void,
    companyName: StringFieldModel,
    orgNo: StringFieldModel,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.searchCompanies = debounce((input: string) => searchCompanies(input), 250);
    this.companyName = companyName;
    this.orgNo = orgNo;
  }

  get disableOrgNo() {
    return Boolean(this.selectedCompany);
  }

  setCompanies(companies: SuggestedCompany[]) {
    this.companies = companies;
  }

  createCustomCompany(name: string): SuggestedCompany | undefined {
    return name !== "" ? { name: name, organizationnumber: "" } : undefined;
  }

  get suggestedCompanies() {
    return compact([this.createCustomCompany(this.companyName.inputValue), ...this.companies]);
  }

  onSelectCompany(company: SuggestedCompany) {
    if (company.organizationnumber) {
      this.selectedCompany = company;
      this.companyName.setAndCommitInputValue(company.name);
      this.orgNo.setAndCommitInputValue(company.organizationnumber);
    } else {
      this.selectedCompany = undefined;
      this.companyName.setAndCommitInputValue(company.name);
      this.orgNo.clear();
    }
  }

  onClearCompany() {
    this.selectedCompany = undefined;
    this.companyName.clear();
    this.orgNo.clear();
    this.companies = [];
  }

  onInputValueChange(value: string) {
    if (this.selectedCompany) {
      this.orgNo.clear();
      this.selectedCompany = undefined;
    }
    this.companyName.setInputValue(value);
    this.searchCompanies(value);
  }
}

export type SubscriberDialogState =
  | { type: "none" }
  | {
      type: "deleteSubscriber";
      subscriberId: SubscriberId;
      submit: (value: SubscriberForIssueModel) => void;
    }
  | { type: "addSigners"; subscriberId: SubscriberId };

export type SubscribersStatus =
  | SubscribersStatusForIssueModel
  | SubscribersStatusForNominalValueModel;

export class SubscribersStatusModel {
  readonly dispatch: DbotEventDispatcher;
  shareholderData: ShareholderData;
  documentLanguages: Language[];
  subscribersModel: SubscribersStatus;
  signersForAllSubscribers: SubscriberSigner[] = [];
  addSignerForAllSubscribersModel?: { nameField: StringFieldModel; emailField: StringFieldModel };

  isLocked: boolean;

  constructor(
    dispatch: DbotEventDispatcher,
    readonly closeDialog: () => void,
    readonly orgNo: string,
    shareholderData: ShareholderData,
    documentLanguages: Language[],
    isLocked: boolean,
    readonly inputState?: InputState,
    data?: IncreaseOfShareCapitalSubscribersStatus,
  ) {
    makeAutoObservable(this, { dispatch: false }, { autoBind: true });
    this.dispatch = dispatch;
    this.isLocked = isLocked;
    this.shareholderData = shareholderData;
    this.documentLanguages = documentLanguages;

    this.subscribersModel = this.toModel(data);
  }

  init(): void {
    this.dispatch({ type: "dbot/fetch-increase-of-share-capital-subscribers-status" });
  }

  handleEvent(event: DbotEvent) {
    switch (event.type) {
      case "dbot/fetch-increase-of-share-capital-subscribers-status-result":
        this.subscribersModel = this.toModel(event.data);
        break;
      default:
        if (this.subscribersModel?.type === IncreaseOfShareCapitalType.shareIssue)
          this.subscribersModel?.handleEvent(event);
        break;
    }
  }

  setSubscribersModel(data?: IncreaseOfShareCapitalSubscribersStatus) {
    this.subscribersModel = this.toModel(data);
    this.signersForAllSubscribers = [];
  }

  addSignerForAllSubscribers() {
    this.addSignerForAllSubscribersModel = {
      nameField: new StringFieldModel({ required: true, validator: validateName }),
      emailField: createEmailField({ required: true }),
    };
  }

  cancelSignerForAllSubscribers() {
    this.addSignerForAllSubscribersModel = undefined;
  }

  submitSignerForAllSubscribers() {
    if (!this.addSignerForAllSubscribersModel) {
      return;
    }
    const { nameField, emailField } = this.addSignerForAllSubscribersModel;
    nameField.validate();
    emailField.validate();
    if (nameField.isValid && emailField.isValid) {
      this.subscribersModel.setDirty();
      this.subscribersModel.subscribers.map((d) =>
        d.addSigner(nameField.validatedValue, emailField.validatedValue),
      );
      this.signersForAllSubscribers.push({
        id: newUuid(),
        name: nameField.validatedValue,
        email: emailField.validatedValue!,
      });
      this.addSignerForAllSubscribersModel = undefined;
    }
  }

  toModel(data?: IncreaseOfShareCapitalSubscribersStatus) {
    switch (data?.type) {
      case IncreaseOfShareCapitalType.increaseNominalValue:
        return new SubscribersStatusForNominalValueModel(
          this.dispatch,
          this.shareholderData,
          this.documentLanguages,
          this.inputState,
          data,
        );
      default:
        return new SubscribersStatusForIssueModel(
          this.dispatch,
          this.orgNo,
          this.shareholderData,
          this.documentLanguages,
          this.inputState,
          data,
        );
    }
  }

  saveAndClose() {
    this.subscribersModel.clearDirty();
    this.subscribersModel.save(this.signersForAllSubscribers);
    this.close();
  }

  close() {
    this.closeDialog();
  }
}

export class SubscribersStatusForNominalValueModel {
  readonly shareholderData: ShareholderData;
  readonly documentLanguages: Language[];
  type: IncreaseOfShareCapitalType.increaseNominalValue;
  contributionPerShareType?: ContributionPerShareType[];
  subscribers: SubscriberForIncreaseNominalValueModel[];
  selectedShareType?: NumberOfSharesForShareType;

  selectedSubscriberDialogState: SubscriberDialogState;

  isDirty = false;

  constructor(
    readonly dispatch: DbotEventDispatcher,
    shareholderData: ShareholderData,
    documentLanguages: Language[],
    readonly inputState?: InputState,
    data?: IncreaseOfShareCapitalSubscribersStatusForNominalValue,
  ) {
    makeAutoObservable(this, { dispatch: false }, { autoBind: true });
    this.shareholderData = shareholderData;
    this.documentLanguages = documentLanguages;
    this.type = IncreaseOfShareCapitalType.increaseNominalValue;
    this.contributionPerShareType = data?.contributionPerShareType;
    this.subscribers =
      data?.subscribers.map(
        (d) =>
          new SubscriberForIncreaseNominalValueModel(
            d,
            this.shareholderData,
            this.documentLanguages,
            this.inputState,
          ),
      ) ?? [];
    this.selectedShareType = this.shareTypes.length > 0 ? this.shareTypes[0] : undefined;

    this.selectedSubscriberDialogState = noDialog();
  }

  get toTableData(): SubscriberStatusIssueRow[] {
    return [
      ...this.subscribers.map((subscriber) => {
        return {
          id: subscriber.subscriberid,
          title: subscriber.nameField.validatedValue,
          subscriptionPrice: this.contributionForSelectedShareType,
          currentShares:
            this.selectedShareType &&
            subscriber.getCurrentNumberOfSharesForShareType(this.selectedShareType.shareTypeId),
          currentOwnership:
            this.selectedShareType &&
            percentOf(
              subscriber.getCurrentNumberOfSharesForShareType(this.selectedShareType.shareTypeId),
              this.currentTotalSharesForShareType,
            ),
          totalPrice: this.getTotalContributionForSubscriber(subscriber),
          subscriber: subscriber,
        };
      }),
      {
        id: "total",
        subscriptionPrice: this.contributionForSelectedShareType,
        currentShares: this.sumOfCurrentSharesForSubscribers,
        totalPrice: this.totalContribution,
      },
    ];
  }

  save(signersForAllSubscribers: SubscriberSigner[]) {
    this.dispatch({
      type: "dbot/addIncreaseOfShareCapitalSubscribersForNominalValue",
      subscribers: this.subscribers.map((d) => toSubscriberForIncreaseNominalValue(d)),
      signersForAllSubscribers,
    });
  }

  get shareTypes() {
    return ensureShareTypeCodeInSharesForShareTypes(
      this.shareholderData.shareTypes,
      this.inputState?.numberOfShares,
    ).filter((d) => d.shareTypeCode !== undefined && d.numberOfShares > 0);
  }

  setSelectedShareType(shareType: NumberOfSharesForShareType) {
    this.selectedShareType = shareType;
  }

  get currentTotalSharesForShareType(): number {
    return this.selectedShareType ? this.selectedShareType.numberOfShares : 0;
  }

  get contributionForSelectedShareType() {
    return (
      this.contributionPerShareType?.find(
        (d) => d.sharetypeId === this.selectedShareType?.shareTypeId,
      )?.contributionPerShare ?? 0
    );
  }

  getTotalContributionForSubscriber(subscriber: SubscriberForIncreaseNominalValueModel) {
    return this.selectedShareType
      ? Big(this.contributionForSelectedShareType)
          .times(
            subscriber.getCurrentNumberOfSharesForShareType(this.selectedShareType.shareTypeId),
          )
          .toNumber()
      : 0;
  }

  get totalContribution() {
    return this.subscribers
      .reduce(
        (sum, subscriber) => sum.plus(this.getTotalContributionForSubscriber(subscriber)),
        Big(0),
      )
      .toNumber();
  }

  get sumOfCurrentSharesForSubscribers() {
    return this.subscribers
      .reduce(
        (sum, subscriber) =>
          sum.plus(
            this.selectedShareType
              ? subscriber.getCurrentNumberOfSharesForShareType(this.selectedShareType.shareTypeId)
              : 0,
          ),
        Big(0),
      )
      .toNumber();
  }

  get selectedSubscriber() {
    switch (this.selectedSubscriberDialogState.type) {
      case "addSigners": {
        const subscriberId = this.selectedSubscriberDialogState.subscriberId;
        return this.subscribers.find((d) => d.subscriberid === subscriberId);
      }
      default:
        return undefined;
    }
  }

  showSubscriberSignerDialog(subscriber: SubscriberForCapitalIncrease) {
    this.selectedSubscriberDialogState = {
      type: "addSigners",
      subscriberId: subscriber.subscriberid,
    };
    if (this.selectedSubscriber?.signers.length === 0) {
      this.selectedSubscriber.addSubscriberAsSigner();
    }
  }

  closeSelectedSubscriberDialog() {
    this.selectedSubscriberDialogState = noDialog();
  }

  setDirty() {
    this.isDirty = true;
  }

  clearDirty() {
    this.isDirty = false;
  }
}

export class SubscribersStatusForIssueModel {
  type: IncreaseOfShareCapitalType.shareIssue;
  shareTypes: ShareTypeForSubscribersStatusModel[];
  subscribers: SubscriberForIssueModel[];
  isPreferentialRights: boolean;
  isOverSubscriptionAllowed: boolean;
  selectedShareType?: ShareTypeForSubscribersStatusModel;

  isAddNewShareholderDialog: boolean = false;
  isAddExistingShareholderDialog: boolean = false;
  newSubscriberForIssue: SubscriberForIssueModel;
  selectedSubscriberDialogState: SubscriberDialogState;
  searchCompany: SearchCompanyModel;
  addShareholdersFromActorsModel?: ActorsSelectorModel;

  isDirty = false;

  constructor(
    readonly dispatch: DbotEventDispatcher,
    readonly orgNo: string,
    readonly shareholderData: ShareholderData,
    readonly documentLanguages: Language[],
    readonly inputState?: InputState,
    data?: IncreaseOfShareCapitalSubscribersStatusForIssue,
  ) {
    makeAutoObservable(this, { dispatch: false }, { autoBind: true });
    this.type = IncreaseOfShareCapitalType.shareIssue;
    this.subscribers =
      data?.subscribers.map(
        (d) =>
          new SubscriberForIssueModel(
            d,
            this.shareholderData,
            this.documentLanguages,
            this.inputState,
          ),
      ) ?? [];
    this.isPreferentialRights = data?.isPreferentialRights ?? false;
    this.isOverSubscriptionAllowed = data?.isOverSubscriptionAllowed ?? false;
    this.shareTypes =
      data?.selectedShareTypes.map(
        (shareType) => new ShareTypeForSubscribersStatusModel(shareType),
      ) ?? [];
    this.selectedShareType = this.shareTypes.length > 0 ? this.shareTypes[0] : undefined;

    this.newSubscriberForIssue = this.createSubscriber();
    this.searchCompany = new SearchCompanyModel(
      this.searchCompanies,
      this.newSubscriberForIssue.nameField,
      this.newSubscriberForIssue.organisationNumber,
    );
    this.selectedSubscriberDialogState = noDialog();
  }

  get existingShareholders() {
    return toExistingShareholders(this.shareholderData.shareholders);
  }

  save(signersForAllSubscribers: SubscriberSigner[]) {
    this.dispatch({
      type: "dbot/addIncreaseOfShareCapitalSubscribersForIssue",
      subscribers: this.subscribers.map((d) => toSubscriberForIssue(d)),
      signersForAllSubscribers,
    });
  }

  handleEvent(event: DbotEvent) {
    switch (event.type) {
      case "dbot/search-companies-result":
        if (event.source === this) {
          this.searchCompany.setCompanies(event.result);
        }
        break;
    }
  }

  searchCompanies(input: string) {
    this.dispatch({ type: "dbot/search-companies", input, source: this });
  }

  get isSubscribersEmpty() {
    return this.subscribers.length === 0;
  }

  setSelectedShareType(value: ShareTypeForSubscribersStatusModel) {
    this.selectedShareType = value;
  }

  get subscribedShareSumType() {
    return this.shareTypes.reduce((map: Record<string, number>, shareType) => {
      map[shareType.sharetypeCode] = this.totalSubscribedSharesForShareType(shareType.sharetypeId);
      return map;
    }, {});
  }

  get sumOfCurrentSharesForSubscribers() {
    return this.subscribers
      .reduce(
        (sum, subscriber) =>
          sum.plus(
            this.selectedShareType
              ? subscriber.getCurrentNumberOfSharesForShareType(this.selectedShareType.sharetypeId)
              : 0,
          ),
        Big(0),
      )
      .toNumber();
  }

  get sumOfNewSharesForSubscribers() {
    return this.sumOfCurrentSharesForSubscribers + this.totalSubscribedShares;
  }

  getShareSumByType(shareTypeId: number) {
    return (
      this.inputState?.numberOfShares.find((d) => d.shareTypeId === shareTypeId)?.numberOfShares ??
      0
    );
  }

  get currentTotalSharesForShareType(): number {
    return this.selectedShareType ? this.getShareSumByType(this.selectedShareType.sharetypeId) : 0;
  }

  get newTotalSharesForShareType() {
    return this.currentTotalSharesForShareType + this.totalSubscribedShares;
  }

  get remainingSharesToSubscribe() {
    return this.selectedShareType
      ? Big(this.selectedShareType.maxNumberOfShares).minus(this.totalSubscribedShares).toNumber()
      : 0;
  }

  totalSubscribedSharesForShareType(shareTypeId: number): number {
    return this.subscribers
      .reduce(
        (sum, subscriber) => sum.plus(subscriber.getNumberOfSharesForShareType(shareTypeId)),
        Big(0),
      )
      .toNumber();
  }

  get totalSubscribedShares() {
    return this.selectedShareType
      ? this.totalSubscribedSharesForShareType(this.selectedShareType.sharetypeId)
      : 0;
  }

  get totalSubscribedPrice() {
    return this.selectedShareType
      ? this.subscribers
          .reduce(
            (sum, subscriber) =>
              sum.plus(
                this.selectedShareType
                  ? subscriber.getTotalPriceForShareType(this.selectedShareType.sharetypeCode)
                  : 0,
              ),
            Big(0),
          )
          .toNumber()
      : 0;
  }

  get selectedSubscriber() {
    switch (this.selectedSubscriberDialogState.type) {
      case "none":
        return undefined;
      case "deleteSubscriber":
      case "addSigners": {
        const subscriberId = this.selectedSubscriberDialogState.subscriberId;
        return this.subscribers.find((d) => d.subscriberid === subscriberId);
      }
    }
  }

  showSubscriberSignerDialog(subscriber: SubscriberForIssueModel) {
    this.selectedSubscriberDialogState = {
      type: "addSigners",
      subscriberId: subscriber.subscriberid,
    };
    if (this.selectedSubscriber?.signers.length === 0) {
      this.selectedSubscriber.addSubscriberAsSigner();
    }
  }

  deleteSubscriber(subscriber: SubscriberForIssueModel) {
    if (subscriber.hasShareSubscriptions) {
      this.selectedSubscriberDialogState = {
        type: "deleteSubscriber",
        subscriberId: subscriber.subscriberid,
        submit: this.deleteSubscriberRow,
      };
    } else {
      this.deleteSubscriberRow(subscriber);
    }
  }

  closeSelectedSubscriberDialog() {
    this.selectedSubscriberDialogState = noDialog();
  }

  deleteSubscriberRow(subscriber: SubscriberForIssueModel) {
    this.setDirty();
    this.subscribers = this.subscribers.filter((value) => value !== subscriber);
    this.closeSelectedSubscriberDialog();
  }

  openAddNewShareholderFromPortfolioDialog() {
    this.addShareholdersFromActorsModel = openAddNewShareholderFromPortfolioDialog(
      this.orgNo,
      this.dispatch,
      this.subscribers,
      this.existingShareholders,
      this.addSubscribers,
      this.closeAddShareholderFromActorsDialog,
    );
  }

  closeAddShareholderFromActorsDialog() {
    this.addShareholdersFromActorsModel = undefined;
  }

  addSubscribers(newSubscribers: SubscriberForIssue[], shareholders?: ExtendedShareholderDTO[]) {
    const selectedSubscribers = newSubscribers.map((s) => this.createSubscriber(s));
    this.subscribers.push(...selectedSubscribers);
    if (shareholders !== undefined) {
      this.shareholderData.shareholders.push(...shareholders);
    }
    this.setDirty();
  }

  openAddNewShareholderDialog(isCompany: boolean) {
    this.newSubscriberForIssue = this.createSubscriber({ isCompany });
    this.searchCompany = new SearchCompanyModel(
      this.searchCompanies,
      this.newSubscriberForIssue.nameField,
      this.newSubscriberForIssue.organisationNumber,
    );
    this.isAddNewShareholderDialog = true;
  }

  submitNewShareholderSubscriber() {
    if (this.newSubscriberForIssue.validate()) {
      this.newSubscriberForIssue.addSubscriberAsSigner();
      this.subscribers = this.subscribers.concat(this.newSubscriberForIssue);
      this.setDirty();
      this.closeAddSubscriberDialog();
    }
  }

  closeAddSubscriberDialog() {
    this.isAddNewShareholderDialog = false;
  }

  private createSubscriber(data?: Partial<SubscriberForIssue>) {
    const subscriber = createSubscriberForIssue({
      ...data,
      subscriptions:
        data?.subscriptions ??
        this.shareTypes.map<Subscription>((s) => ({
          id: newUuid(),
          sharetypeId: s.sharetypeId,
          shareTypeCode: s.sharetypeCode,
          numberOfShares: 0,
          subscriptionPrice: s.sharePrice,
        })),
    });
    return new SubscriberForIssueModel(
      subscriber,
      this.shareholderData,
      this.documentLanguages,
      this.inputState,
    );
  }

  setDirty() {
    this.isDirty = true;
  }

  clearDirty() {
    this.isDirty = false;
  }
}

export class ShareTypeForSubscribersStatusModel {
  sharetypeId: number;
  sharetypeCode: ShareTypeCode;
  fixed?: Fixed;
  interval?: Interval;
  issueMode: ShareIssueMode;
  sharePrice: number;

  constructor(public data: ShareIssuePerShareType) {
    this.sharetypeId = data.sharetypeId;
    this.sharetypeCode = data.sharetypeCode;
    this.fixed = asFixed(data.issueMode);
    this.interval = asInterval(data.issueMode);
    this.issueMode = data.issueMode;
    this.sharePrice = data.sharePrice ?? 0;
    makeAutoObservable(this);
  }

  get isFixed() {
    return this.issueMode.type === "fixed";
  }

  get minNumberOfShares(): number {
    switch (this.issueMode.type) {
      case "fixed":
        return this.issueMode.numberOfShares ?? 0;
      case "interval":
        return this.issueMode.from ?? 0;
    }
  }

  get maxNumberOfShares(): number {
    switch (this.issueMode.type) {
      case "fixed":
        return this.issueMode.numberOfShares ?? 0;
      case "interval":
        return this.issueMode.to ?? 0;
    }
  }
}

export function toProperties(model: ShareCapitalIncreaseTypeModel): ShareCapitalIncreaseType {
  if (model.type === "shareIssue") {
    return toShareIssue(model);
  } else {
    return toIncreaseNominalValue(model);
  }
}

function toIncreaseNominalValue(model: IncreaseNominalValueModel): IncreaseNominalValue {
  return {
    type: model.type,
    increaseInNominalValue: model.increaseInNominalValue.toNumber(),
    sharePrice: model.sharePrice.toNumber(),
    contributionPerShareTypes: model.contributionPerShareTypes.map((s) => s.toModel()),
    subscribers: model.subscribers.map((s) => toSubscriberForIncreaseNominalValue(s)),
  };
}

function toShareIssueForShareTypes(model: ShareIssueModel): ShareIssuePerShareType[] {
  return model.selectedShareTypes.selected.map((s) => {
    return {
      sharetypeId: s.sharetypeId,
      sharetypeCode: s.sharetypeCode,
      isNewShareType: s.isNewShareType,
      issueMode: toShareIssueMode(s.model.issueMode),
      sharePrice: s.model.sharePrice.toNumber(),
    };
  });
}

function toShareIssue(model: ShareIssueModel): ShareIssue {
  return {
    type: model.type,
    nominalValue: model.nominalValue.toNumber(),
    preferentialRight: model.preferentialRight.value,
    preferentialRightTransferable: model.preferentialRightTransferable.value,
    preferentialRightOversubscribe: model.preferentialRightOversubscribe.value,
    preferentialBoardRightsToSellSubscription:
      model.preferentialBoardRightsToSellSubscription.value,
    externalSubscribers: model.externalSubscribers.value,
    preferentialRightWaivedReason: model.preferentialRightWaivedReasonModel.value,
    preferentialRightWaivedReasonText: model.preferentialRightWaivedReasonText.validatedValue,
    shareholderRights: model.shareholderRights.value,
    isGuaranteeConsortiumEstablished: model.isGuaranteeConsortiumEstablished.value,
    guaranteeConsortiumCommission: toGuaranteeConsortiumCommission(
      model.guaranteeConsortiumCommission,
    ),
    shareIssuePerShareTypes: toShareIssueForShareTypes(model),
    subscribers: model.subscribers.map((s) => toSubscriberForIssue(s)),
    preferentialIssueSubscribers: model.preferentialIssueSubscribers.map((s) =>
      toSubscriberForIssue(s),
    ),
  };
}

function toSubscriberForIssue(s: SubscriberForIssueModel): SubscriberForIssue {
  return {
    ...s.data,
    name: s.nameField.validatedValue,
    email: s.email.validatedValue,
    address: s.address.validatedValue,
    organisationNumber: s.isCompany ? s.organisationNumber.validatedValue : undefined,
    contactName: s.isCompany ? s.contactName.validatedValue : undefined,
    debtContribution: s.toDebtContribution(),
    kindContribution: s.toKindContribution(),
    cashContribution: s.toCashContribution(),
    subscriptions: s.subscriptions.map((d) => toSubscriptionForIssue(d)),
    signers: s.signers.map((d) => d.toSubscriberSigner()),
  };
}

function toSubscriptionForIssue(s: SubscriptionForIssueModel): Subscription {
  return {
    id: s.id,
    sharetypeId: s.sharetypeId,
    shareTypeCode: s.shareTypeCode,
    numberOfShares: s.numberOfShares.toNumberOrZero(),
    subscriptionPrice: s.subscriptionPrice.toNumberOrZero(),
  };
}

function toSubscriberForIncreaseNominalValue(
  s: SubscriberForIncreaseNominalValueModel,
): SubscriberForIncreaseNominalValue {
  return {
    ...s.data,
    name: s.nameField.inputValue,
    address: s.address.validatedValue,
    debtContribution: s.toDebtContribution(),
    kindContribution: s.toKindContribution(),
    cashContribution: s.toCashContribution(),
    signers: s.signers.map((d) => d.toSubscriberSigner()),
  };
}

function toShareIssueMode(model: ShareIssueModeModel): ShareIssueMode {
  if (model.type === "interval") {
    return toShareIssueInterval(model);
  } else {
    return toShareIssueFixed(model);
  }
}

function toShareIssueFixed(model: FixedModel): Fixed {
  return {
    type: model.type,
    numberOfShares: model.numberOfShares.toNumber(),
  };
}

function toShareIssueInterval(model: IntervalModel): Interval {
  return {
    type: model.type,
    from: model.from.toNumber(),
    to: model.to.toNumber(),
  };
}

export function toPeriodForSubscription(model: PeriodForSubscriptionModel): PeriodForSubscription {
  return {
    from: model.from.toISOString(),
    to: model.to.toISOString(),
  };
}

function toGuaranteeConsortiumCommission(
  model: GuaranteeConsortiumCommissionModel,
): GuaranteeConsortiumCommission {
  return {
    numberOfShares: model.numberOfShares.toNumber(),
    percentOfGuaranteedAmount: model.percentOfGuaranteedAmount.toNumber(),
  };
}

export type ProRataAllocation = {
  shareholder: ExtendedShareholderDTO;
  shareType: ShareTypeOption;
  numberOfSharesToIssue: number;
  fractionShare: number;
};

export function subscribersForIssueFromProRataCalculation(
  data: SubscriberForIssue[],
  shareTypes: ShareTypeOption[],
  totalExistingShares: number,
  shareholders: ExtendedShareholderDTO[],
  sharesForShareholders: SharesForShareholder[],
): SubscriberForIssue[] {
  const allocations = shareTypes.flatMap((shareType) =>
    proRataCalculation(shareType, totalExistingShares, shareholders, sharesForShareholders),
  );
  const uniqueShareholders = Array.from(new Set(allocations.map((d) => d.shareholder)));
  return uniqueShareholders.map((shareholder) => {
    const currentData = data.find((d) => d.id === shareholder.id);
    return {
      id: shareholder.id,
      subscriberid: currentData?.subscriberid ?? newUuid(),
      name: shareholder.name,
      email: currentData?.email ?? shareholder.email,
      organisationNumber: shareholder.isCompany ? shareholder.organizationNumber : undefined,
      isExisting: true,
      isCompany: shareholder.isCompany,
      contactName:
        currentData?.contactName ??
        (shareholder.isCompany ? getFullName(shareholder.contactInfo) : undefined),
      cashContribution: currentData?.cashContribution,
      debtContribution: currentData?.debtContribution,
      kindContribution: currentData?.kindContribution,
      subscriptions: allocations
        .filter((d) => d.shareholder.id === shareholder.id)
        .map((allocation) => {
          return {
            id: newUuid(),
            sharetypeId: allocation.shareType.sharetypeId,
            shareTypeCode: allocation.shareType.sharetypeCode,
            numberOfShares: allocation.numberOfSharesToIssue,
            subscriptionPrice: allocation.shareType.model.sharePrice.toNumberOrZero(),
          };
        }),
      signers: currentData?.signers ?? [getShareholderSubscriberSigner(shareholder)],
    };
  });
}

export function proRataCalculation(
  shareType: ShareTypeOption,
  totalExistingShares: number,
  shareholders: ExtendedShareholderDTO[],
  sharesForShareholders: SharesForShareholder[],
): ProRataAllocation[] {
  const totalIssuedPerShareType = shareType.model.maxNumberOfShares;
  const activeShareholders = shareholders.filter((s) =>
    sharesForShareholders.some((share) => share.shareholderId === s.id && share.numberOfShares > 0),
  );
  const allocatedShares: ProRataAllocation[] = activeShareholders.map((shareholder) => {
    const numberOfSharesForShareholder = sharesForShareholders
      .filter((share) => share.shareholderId === shareholder.id)
      .reduce((sum, share) => sum + share.numberOfShares, 0);
    const ownership = Big(numberOfSharesForShareholder).div(totalExistingShares);
    const allocated = Big(totalIssuedPerShareType).mul(ownership);
    const sharesWithoutFraction = allocated.round(0, Big.roundDown);
    return {
      shareholder: shareholder,
      shareType: shareType,
      numberOfSharesToIssue: sharesWithoutFraction.toNumber(),
      fractionShare: allocated.minus(sharesWithoutFraction).toNumber(),
    };
  });
  const remainingShares =
    totalIssuedPerShareType -
    allocatedShares.reduce((sum, value) => sum + value.numberOfSharesToIssue, 0);
  return addRemainingSharesByHighestFractionForProRataCalculation(allocatedShares, remainingShares);
}

export function addRemainingSharesByHighestFractionForProRataCalculation(
  allocation: ProRataAllocation[],
  remainingShares: number,
): ProRataAllocation[] {
  return allocation
    .sort((a, b) => b.fractionShare - a.fractionShare)
    .map((allocation, index) => {
      return {
        shareholder: allocation.shareholder,
        shareType: allocation.shareType,
        numberOfSharesToIssue:
          index < remainingShares
            ? allocation.numberOfSharesToIssue + 1
            : allocation.numberOfSharesToIssue,
        fractionShare: 0,
      };
    });
}

export interface SubscriberStatusIssueRow {
  id: string;
  title?: string;
  numberOfShares?: number;
  subscriptionPrice?: number;
  totalPrice?: number;
  currentShares?: number;
  currentOwnership?: number;
  newShares?: number;
  newOwnership?: number;
  subscriber?: SubscriberForCapitalIncrease;
}

export interface SubscriberAllocationRow {
  id: string;
  title?: ReactNode;
  offeredShares?: number;
  subscribedShares?: number;
  allocatedShares: NumberFieldModel;
  subscriptionPrice?: number;
  totalPrice: number;
  currentOwnership: number;
  newOwnership: number;
  currentNumberOfShares: number;
  newNumberOfShares: number;
}

export function getExistingSubscribersForIncreaseNominalValue(
  shareholders: ExtendedShareholderDTO[],
  data: SubscriberForIncreaseNominalValue[],
): SubscriberForIncreaseNominalValue[] {
  return data.filter((sub) => shareholders.some((s) => sub.shareholderId === s.id));
}

export function getNewShareholderSubscribersForIncreaseNominalValue(
  shareholders: ExtendedShareholderDTO[],
  data: SubscriberForIncreaseNominalValue[],
): SubscriberForIncreaseNominalValue[] {
  return shareholders
    .filter((s) => !data?.some((sub) => sub.shareholderId === s.id))
    .map((s) => ({
      subscriberid: newUuid(),
      shareholderId: s.id,
      name: s.name,
      signers: [getShareholderSubscriberSigner(s)],
    }));
}

export function getShareholderSubscriberSigner(
  shareholder: ExtendedShareholderDTO,
): SubscriberSigner {
  const contactName = getFullName(shareholder.contactInfo);
  const name = shareholder.isCompany && isNotBlank(contactName) ? contactName : shareholder.name;
  return { id: newUuid(), name: name, email: shareholder.email };
}

export function getSubscribersForIncreaseNominalValue(
  shareholders: ExtendedShareholderDTO[],
  data: SubscriberForIncreaseNominalValue[],
): SubscriberForIncreaseNominalValue[] {
  //we gotta convert all active shareholders to the model, cause for increase of nominal value all existing shareholders
  //should participate
  const shareholderSubscribers = getNewShareholderSubscribersForIncreaseNominalValue(
    shareholders,
    data,
  );
  const existingSubscribers = getExistingSubscribersForIncreaseNominalValue(shareholders, data);
  return existingSubscribers.concat(shareholderSubscribers);
}

export function existingShareholderText(n: number): TMessage | undefined {
  return n > 1
    ? { key: "subscribers.valid.existingShareholder.plural", params: { n: n } }
    : n === 1
      ? { key: "subscribers.valid.existingShareholder.singular", params: { n: n } }
      : undefined;
}

export function newShareholderText(n: number): TMessage | undefined {
  return n > 1
    ? { key: "subscribers.valid.newShareholder.plural", params: { n: n } }
    : n === 1
      ? { key: "subscribers.valid.newShareholder.singular", params: { n: n } }
      : undefined;
}

export function formatValidSubscribersLabel(
  activeSubscribersForIssue: SubscriberForIssueModel[],
): TMessage {
  const existing = activeSubscribersForIssue.filter(
    (d) => d.isExisting && d.hasShareSubscriptions,
  ).length;
  const others = activeSubscribersForIssue.filter(
    (d) => !d.isExisting && d.hasShareSubscriptions,
  ).length;
  const texts = compact([existingShareholderText(existing), newShareholderText(others)]);
  return {
    key: "subscribers.valid",
    params: { shareholders: texts },
  };
}

export function subscriberSignerLabel(subscriber: SubscriberForCapitalIncrease): TMessage {
  return subscriber.isCompany && subscriber.isSignerSelf
    ? { key: "add" }
    : !subscriber.isCompany && subscriber.isSignerSelf
      ? { key: "decisions.increaseShareCapital.subscribersStatus.signers.self" }
      : { translated: subscriber.signers.length.toString() };
}

export function shareholderHasShares(
  shareholder: ExtendedShareholderDTO,
  sharesForShareholders?: SharesForShareholder[],
): boolean {
  return (
    sharesForShareholders?.some(
      (share) => share.shareholderId === shareholder.id && share.numberOfShares > 0,
    ) ?? hasShares(shareholder)
  );
}

function isActorExistingShareholder(actor: SelectedActor, shareholder: ExistingShareholder) {
  return (
    (actor.type === ActorType.SHAREHOLDER &&
      actor.reference === toShareholderReference(shareholder.id)) ||
    (actor.companyNumber !== undefined && actor.companyNumber === shareholder.companyNumber) ||
    (actor.socialSecurityNumber !== undefined &&
      actor.socialSecurityNumber === shareholder.socialSecurityNumber)
  );
}

function createSubscriberForIssue(data: Partial<SubscriberForIssue>): SubscriberForIssue {
  return {
    ...data,
    subscriptions: data.subscriptions ?? [],
    signers: [],
    id: data?.id ?? -new Date().valueOf(),
    subscriberid: newUuid(),
    name: data?.name ?? "",
    email: data?.email,
    isExisting: data?.isExisting ?? false,
    isCompany: data?.isCompany ?? false,
    contactName: data?.contactName,
  };
}

export function mapExistingShareholdersToSubscriberForIssue(
  shareholders: ExistingShareholder[],
): SubscriberForIssue[] {
  return shareholders.map(({ isCompany, id, name, email, contactName }) =>
    createSubscriberForIssue({ isCompany, id, name, email, isExisting: true, contactName }),
  );
}

function mapShareholderDTOToSubscriberForIssue(
  shareholders: ExtendedShareholderDTO[],
): SubscriberForIssue[] {
  return shareholders.map(({ isCompany, id, name, email, contactInfo }) =>
    createSubscriberForIssue({
      isCompany,
      id,
      name,
      email,
      isExisting: true,
      contactName: isCompany ? getFullName(contactInfo) : undefined,
    }),
  );
}

function toExistingShareholders(shareholders: ExtendedShareholderDTO[]): ExistingShareholder[] {
  return shareholders.map((s) => ({
    id: s.id,
    name: s.name,
    email: s.email,
    isCompany: s.isCompany,
    isSelected: false,
    contactName: s.isCompany ? getFullName(s.contactInfo) : undefined,
    socialSecurityNumber: s.personInfo?.socialSecurityNumber,
    companyNumber: s.companyNumber,
  }));
}

function openAddNewShareholderFromPortfolioDialog(
  orgNo: string,
  dispatch: DbotEventDispatcher,
  subscribers: SubscriberForIssueModel[],
  shareholders: ExistingShareholder[],
  addSubscribers: (
    newSubscribers: SubscriberForIssue[],
    shareholders?: ExtendedShareholderDTO[],
  ) => void,
  onClose: () => void,
): ActorsSelectorModel {
  return new ActorsSelectorModel({
    title: { key: "shareholders.add-shareholders" },
    organisationNumber: orgNo,
    tabs: [ActorType.SHAREHOLDER, ActorType.BOARD_MEMBER, ActorType.CEO],
    excludeActors: subscribers
      .filter((a) => a.isExisting)
      .map(
        (shareholder) =>
          shareholder.ssnOrCompanyNumber ?? toShareholderReference(shareholder.shareholderId),
      ),
    onLoad: (type, onSuccess, onFailure) => loadActors(type, onSuccess, onFailure, dispatch, orgNo),
    onSubmit: (selectedActors, onError) => {
      const [rest, newShareholders] = partition(selectedActors, (actor) =>
        shareholders.some((s) => isActorExistingShareholder(actor, s)),
      );
      const existingShareholders = rest.map(
        (actor) => shareholders.find((s) => isActorExistingShareholder(actor, s))!,
      );
      addSubscribers(
        mapExistingShareholdersToSubscriberForIssue([...new Set(existingShareholders)]),
      );

      if (newShareholders.length > 0) {
        dispatch(
          actorsApi.addShareholdersFromActors({
            orgNo: orgNo,
            data: newShareholders,
            onSuccess: (result) => {
              const newShareholders = result.map((s) => transformShareholder(s));
              addSubscribers(
                mapShareholderDTOToSubscriberForIssue(newShareholders),
                newShareholders,
              );
              onClose();
              return [];
            },
            onFailure: () => {
              onError();
              return [];
            },
          }),
        );
      } else {
        onClose();
      }
    },
    onClose,
    isMultiSelect: true,
  });
}

export class QuickSetupModel {
  type: QuickSetupType;
  showSetupPanel: boolean = true;
  selectedTab: QuickSetupTab = "quickEdit";

  constructor(type: QuickSetupType) {
    this.type = type;
    makeAutoObservable(this, {}, { autoBind: true });
  }

  setSelectedTab(tab: QuickSetupTab) {
    this.selectedTab = tab;
  }

  get tabs() {
    return Object.entries(quickSetupTabs).map(([id, value]) => ({
      id: id,
      label: value,
      isActive: this.selectedTab === id,
      onSelect: () => this.setSelectedTab(id as QuickSetupTab),
    }));
  }

  get showQuickSetupTab() {
    return this.selectedTab === "quickEdit";
  }

  toggleSetupPanel() {
    this.setSelectedTab("quickEdit");
    this.showSetupPanel = !this.showSetupPanel;
  }
}

export type QuickSetupTab = "quickEdit" | "about";

const quickSetupTabs: { [key in QuickSetupTab]: TMessage } = {
  quickEdit: { key: "quick-edit" },
  about: { key: "decisions.increaseShareCapital.quickSetup.about" },
} as const;

function quickSetupSuccessMessage(type?: QuickSetupType): TMessage {
  return type === undefined
    ? { key: "decisions.increaseShareCapital.quickSetupSelector.removed.success" }
    : { key: "decisions.increaseShareCapital.quickSetupSelector.added.success" };
}

export const quickSetupTypeLabels: { [key in QuickSetupType]: TMessage } = {
  [QuickSetupType.CASH_PREFERENTIAL_RIGHTS]: {
    key: "decisions.increaseShareCapital.quickSetupSelector.types.cashPreferentialRights",
  },
  [QuickSetupType.CASH_PRIVATE_PLACEMENT]: {
    key: "decisions.increaseShareCapital.quickSetupSelector.types.cashPrivatePlacement",
  },
};

export const quickSetupTypeTooltip: { [key in QuickSetupType]: TMessage } = {
  [QuickSetupType.CASH_PREFERENTIAL_RIGHTS]: {
    key: "decisions.increaseShareCapital.quickSetupSelector.types.cashPreferentialRights.tooltip",
  },
  [QuickSetupType.CASH_PRIVATE_PLACEMENT]: {
    key: "decisions.increaseShareCapital.quickSetupSelector.types.cashPrivatePlacement.tooltip",
  },
};

export const quickSetupAbout: { [key in QuickSetupType]: TMessage } = {
  [QuickSetupType.CASH_PREFERENTIAL_RIGHTS]: {
    key: "decisions.increaseShareCapital.quickSetup.cashPreferentialRights.about",
  },
  [QuickSetupType.CASH_PRIVATE_PLACEMENT]: {
    key: "decisions.increaseShareCapital.quickSetup.cashPrivatePlacement.about",
  },
};

export function filterSubscribersOnSelectedShareTypes(
  subscribers: SubscriberForIssue[],
  selectedShareTypes: ShareIssuePerShareType[],
): SubscriberForIssue[] {
  return subscribers
    .map((subscriber) => ({
      ...subscriber,
      subscriptions: subscriber.subscriptions.filter((subscription) =>
        selectedShareTypes.some((shareType) => shareType.sharetypeId === subscription.sharetypeId),
      ),
    }))
    .filter(
      (subscriber) =>
        subscriber.subscriptions.length > 0 &&
        subscriber.subscriptions.some((a) => a.numberOfShares > 0),
    );
}
